import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { dashboardAPI, citiesAPI, userActivityAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Button,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
} from '@mui/material';
import {
  People,
  LocationCity,
  Event,
  Apartment,
  LocalOffer,
  ContactPhone,
  Timeline,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  Close,
  ShowChart,
  BarChart,
  PieChart,
  TrendingUp,
  KeyboardArrowDown,
} from '@mui/icons-material';

const MONTHS_OPTIONS = [
  { value: 'all', labelKey: 'dashboard.allMonths', defaultLabel: 'All Months', shortLabel: 'All' },
  { value: '1', labelKey: 'months.january', defaultLabel: 'January', shortLabel: 'Jan' },
  { value: '2', labelKey: 'months.february', defaultLabel: 'February', shortLabel: 'Feb' },
  { value: '3', labelKey: 'months.march', defaultLabel: 'March', shortLabel: 'Mar' },
  { value: '4', labelKey: 'months.april', defaultLabel: 'April', shortLabel: 'Apr' },
  { value: '5', labelKey: 'months.may', defaultLabel: 'May', shortLabel: 'May' },
  { value: '6', labelKey: 'months.june', defaultLabel: 'June', shortLabel: 'Jun' },
  { value: '7', labelKey: 'months.july', defaultLabel: 'July', shortLabel: 'Jul' },
  { value: '8', labelKey: 'months.august', defaultLabel: 'August', shortLabel: 'Aug' },
  { value: '9', labelKey: 'months.september', defaultLabel: 'September', shortLabel: 'Sep' },
  { value: '10', labelKey: 'months.october', defaultLabel: 'October', shortLabel: 'Oct' },
  { value: '11', labelKey: 'months.november', defaultLabel: 'November', shortLabel: 'Nov' },
  { value: '12', labelKey: 'months.december', defaultLabel: 'December', shortLabel: 'Dec' },
];

function getMonthDisplayLabel(val, period, t) {
  if (period !== 'month') return t('dashboard.periodMonth') || 'Month';
  if (!val || val === 'all') return t('dashboard.allMonths') || 'All Months';
  const item = MONTHS_OPTIONS.find((m) => m.value === String(val));
  return item ? (t(item.labelKey) || item.defaultLabel || item.shortLabel) : (t('dashboard.periodMonth') || 'Month');
}

const statShape = PropTypes.shape({
  total: PropTypes.number,
  totalUsers: PropTypes.number,
  totalCities: PropTypes.number,
  totalEvents: PropTypes.number,
  totalPromotions: PropTypes.number,
  totalContacts: PropTypes.number,
  totalAccommodations: PropTypes.number,
  active: PropTypes.number,
  activeUsers: PropTypes.number,
  activeCities: PropTypes.number,
  activeEvents: PropTypes.number,
  activePromotions: PropTypes.number,
  activeContacts: PropTypes.number,
  activeAccommodations: PropTypes.number,
  newThisMonth: PropTypes.number,
  newUsersThisMonth: PropTypes.number,
  newCitiesThisMonth: PropTypes.number,
  newEventsThisMonth: PropTypes.number,
  newPromotionsThisMonth: PropTypes.number,
  newContactsThisMonth: PropTypes.number,
  newAccommodationsThisMonth: PropTypes.number,
  pending: PropTypes.number,
  pendingEvents: PropTypes.number,
  pendingPromotions: PropTypes.number,
  pendingContacts: PropTypes.number,
  pendingAccommodations: PropTypes.number,
  trend: PropTypes.string,
  percentageChange: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currentMonthPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lastMonthPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
});

function pickStatValue(stats, keys) {
  if (!stats) return 0;
  for (const key of keys) {
    const value = stats[key];
    if (value != null && value !== '') return value;
  }
  return 0;
}

function getStatTotal(stats) {
  return pickStatValue(stats, [
    'total',
    'totalUsers',
    'totalCities',
    'totalEvents',
    'totalPromotions',
    'totalContacts',
    'totalAccommodations',
  ]);
}

function getStatActive(stats) {
  return pickStatValue(stats, [
    'active',
    'activeUsers',
    'activeCities',
    'activeEvents',
    'activePromotions',
    'activeContacts',
    'activeAccommodations',
  ]);
}

function getStatNewThisMonth(stats) {
  return pickStatValue(stats, [
    'newThisMonth',
    'newUsersThisMonth',
    'newCitiesThisMonth',
    'newEventsThisMonth',
    'newPromotionsThisMonth',
    'newContactsThisMonth',
    'newAccommodationsThisMonth',
  ]);
}

function getStatPending(stats) {
  return pickStatValue(stats, [
    'pending',
    'pendingEvents',
    'pendingPromotions',
    'pendingContacts',
    'pendingAccommodations',
  ]);
}

function getStatApproved(stats) {
  return pickStatValue(stats, [
    'approved',
    'approvedEvents',
    'approvedPromotions',
    'approvedContacts',
    'approvedAccommodations',
  ]);
}

function getStatRejected(stats) {
  return pickStatValue(stats, [
    'rejected',
    'rejectedEvents',
    'rejectedPromotions',
    'rejectedContacts',
    'rejectedAccommodations',
  ]);
}

function getStatInactive(stats) {
  return pickStatValue(stats, [
    'inactive',
    'inactiveUsers',
    'inactiveCities',
    'inactiveEvents',
    'inactivePromotions',
    'inactiveContacts',
    'inactiveAccommodations',
  ]);
}

function getStatNewThisPeriod(stats) {
  return pickStatValue(stats, [
    'newThisPeriod',
    'newUsersThisPeriod',
    'newCitiesThisPeriod',
    'newEventsThisPeriod',
    'newPromotionsThisPeriod',
    'newContactsThisPeriod',
    'newAccommodationsThisPeriod',
    'newThisMonth',
    'newUsersThisMonth',
    'newCitiesThisMonth',
    'newEventsThisMonth',
    'newPromotionsThisMonth',
    'newContactsThisMonth',
    'newAccommodationsThisMonth',
  ]);
}

function getActivityStatusChipColor(status) {
  if (status === 'APPROVED') return 'success';
  if (status === 'PENDING') return 'warning';
  return 'error';
}

function getTrendIcon(trend) {
  if (trend === 'up') return <ArrowUpward sx={{ fontSize: 13 }} />;
  if (trend === 'down') return <ArrowDownward sx={{ fontSize: 13 }} />;
  return null;
}

function getTrendColor(trend) {
  if (trend === 'up') return '#10B981';
  if (trend === 'down') return '#EF4444';
  return '#64748B';
}

function formatDashboardError(error, translate) {
  if (error?.startsWith('dashboard.')) return translate(error);
  return error;
}

function getDateLocale(locale) {
  return locale === 'de' ? 'de-DE' : 'en-US';
}

function formatDateTime(dateStr, locale) {
  if (!dateStr) return 'Just now';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 2000) return 'Just now';
  const day = d.getDate().toString().padStart(2, '0');
  const month = d.toLocaleString(getDateLocale(locale), { month: 'short' });
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

// Sparkline Mini-Chart for Stat Cards with Glow & Endpoint Dot
function MiniSparkline({ color = '#0F3D3E', trend = 'up' }) {
  const safeId = useMemo(() => `spark-${color.replace(/[^a-zA-Z0-9]/g, '')}`, [color]);

  const pathD = trend === 'down'
    ? "M 0,10 Q 30,8 55,20 T 90,26 T 120,30"
    : "M 0,30 C 25,28 40,10 60,18 C 80,24 95,8 120,10";

  const areaD = trend === 'down'
    ? "M 0,10 Q 30,8 55,20 T 90,26 T 120,30 L 120,38 L 0,38 Z"
    : "M 0,30 C 25,28 40,10 60,18 C 80,24 95,8 120,10 L 120,38 L 0,38 Z";

  const endX = 120;
  const endY = trend === 'down' ? 30 : 10;

  return (
    <svg width="100%" height="38" viewBox="0 0 120 38" style={{ overflow: 'visible', display: 'block' }}>
      <defs>
        <linearGradient id={safeId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.0" />
        </linearGradient>
        <filter id={`glow-${safeId}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor={color} floodOpacity="0.35" />
        </filter>
      </defs>
      <path d={areaD} fill={`url(#${safeId})`} />
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#glow-${safeId})`}
      />
      <circle cx={endX} cy={endY} r="4" fill={color} stroke="#ffffff" strokeWidth="2" />
    </svg>
  );
}

MiniSparkline.propTypes = {
  color: PropTypes.string,
  trend: PropTypes.string,
};

const StatCard = ({
  title,
  stats,
  icon,
  color = '#0F3D3E',
  gradient = 'linear-gradient(135deg, #0F3D3E 0%, #165658 100%)',
  onClick,
  t,
}) => {
  const total = getStatTotal(stats);
  const approved = getStatApproved(stats);
  const active = getStatActive(stats);
  const pending = getStatPending(stats);
  const rejected = getStatRejected(stats);
  const inactive = getStatInactive(stats);
  const newThisPeriod = getStatNewThisPeriod(stats);

  const rawPercentage = stats?.percentageChange ?? 0;
  const trend = stats?.trend || (rawPercentage < 0 ? 'down' : 'up');

  const formattedPerc = typeof rawPercentage === 'number'
    ? (rawPercentage > 0 ? `+${rawPercentage}%` : `${rawPercentage}%`)
    : rawPercentage;

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '20px',
        bgcolor: '#ffffff',
        border: '1px solid #E2E8F0',
        boxShadow: '0 4px 18px -2px rgba(15, 23, 42, 0.04), 0 2px 6px -1px rgba(15, 23, 42, 0.02)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 14px 30px -4px rgba(15, 23, 42, 0.12), 0 6px 12px -2px rgba(15, 23, 42, 0.06)',
          borderColor: color,
        },
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2 } }}>
        {/* Top Header Row */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar
              sx={{
                background: gradient,
                color: '#ffffff',
                width: 38,
                height: 38,
                borderRadius: '12px',
                boxShadow: `0 4px 12px ${color}33`,
              }}
            >
              {icon}
            </Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '0.92rem', letterSpacing: '-0.01em' }}>
              {title}
            </Typography>
          </Box>

          <Chip
            icon={getTrendIcon(trend)}
            label={formattedPerc}
            size="small"
            sx={{
              bgcolor: trend === 'up' ? '#ECFDF5' : trend === 'down' ? '#FEF2F2' : '#F8FAFC',
              color: trend === 'up' ? '#059669' : trend === 'down' ? '#DC2626' : '#64748B',
              border: `1px solid ${trend === 'up' ? '#A7F3D0' : trend === 'down' ? '#FCA5A5' : '#E2E8F0'}`,
              fontWeight: 800,
              fontSize: '0.7rem',
              height: 22,
              px: 0.5,
              '& .MuiChip-icon': {
                color: 'inherit',
                ml: 0.5,
              },
            }}
          />
        </Box>

        {/* Main Stat & Mini Sparkline Row */}
        <Box display="flex" alignItems="flex-end" justifyContent="space-between" mb={1.75}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 900, color: '#0F172A', lineHeight: 1, letterSpacing: '-0.02em', fontSize: '1.95rem' }}>
              {total.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.72rem', mt: 0.35, display: 'block' }}>
              {t('dashboard.total')}
            </Typography>
          </Box>

          <Box sx={{ width: 100, height: 34, mb: 0.5 }}>
            <MiniSparkline color={color} trend={trend} />
          </Box>
        </Box>

        {/* Sub-Metrics 6-Box Grid */}
        <Grid container spacing={0.75}>
          <Grid item xs={4}>
            <Box
              sx={{
                bgcolor: '#ECFDF5',
                border: '1px solid #A7F3D0',
                p: 0.75,
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ color: '#047857', fontSize: '0.6rem', display: 'block', fontWeight: 700, textTransform: 'uppercase' }} noWrap>
                {t('dashboard.approved') || 'Approved'}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#059669', lineHeight: 1.1, mt: 0.2, fontSize: '0.85rem' }}>
                {approved}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box
              sx={{
                bgcolor: '#F0FDF4',
                border: '1px solid #DCFCE7',
                p: 0.75,
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ color: '#166534', fontSize: '0.6rem', display: 'block', fontWeight: 700, textTransform: 'uppercase' }} noWrap>
                {t('dashboard.active') || 'Active'}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#15803D', lineHeight: 1.1, mt: 0.2, fontSize: '0.85rem' }}>
                {active}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box
              sx={{
                bgcolor: '#FFFBEB',
                border: '1px solid #FEF3C7',
                p: 0.75,
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ color: '#92400E', fontSize: '0.6rem', display: 'block', fontWeight: 700, textTransform: 'uppercase' }} noWrap>
                {t('dashboard.pending') || 'Pending'}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#B45309', lineHeight: 1.1, mt: 0.2, fontSize: '0.85rem' }}>
                {pending}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box
              sx={{
                bgcolor: '#FEF2F2',
                border: '1px solid #FCA5A5',
                p: 0.75,
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ color: '#991B1B', fontSize: '0.6rem', display: 'block', fontWeight: 700, textTransform: 'uppercase' }} noWrap>
                {t('dashboard.rejected') || 'Rejected'}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#DC2626', lineHeight: 1.1, mt: 0.2, fontSize: '0.85rem' }}>
                {rejected}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box
              sx={{
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                p: 0.75,
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ color: '#475569', fontSize: '0.6rem', display: 'block', fontWeight: 700, textTransform: 'uppercase' }} noWrap>
                {t('dashboard.inactive') || 'Inactive'}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#64748B', lineHeight: 1.1, mt: 0.2, fontSize: '0.85rem' }}>
                {inactive}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box
              sx={{
                bgcolor: '#F0F9FF',
                border: '1px solid #E0F2FE',
                p: 0.75,
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ color: '#075985', fontSize: '0.6rem', display: 'block', fontWeight: 700, textTransform: 'uppercase' }} noWrap>
                {t('dashboard.newThisPeriod') || 'New'}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0369A1', lineHeight: 1.1, mt: 0.2, fontSize: '0.85rem' }}>
                {newThisPeriod}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  stats: statShape,
  icon: PropTypes.node,
  color: PropTypes.string,
  gradient: PropTypes.string,
  onClick: PropTypes.func,
  t: PropTypes.func.isRequired,
};

function DashboardStatsCards({
  stats,
  statsPeriod,
  statsMonth,
  statsYear,
  onPeriodChange,
  onMonthChange,
  onYearChange,
  t,
  onOpenCityDialog
}) {
  const [monthAnchorEl, setMonthAnchorEl] = useState(null);
  const [yearAnchorEl, setYearAnchorEl] = useState(null);

  const availableYears = useMemo(() => {
    const startYear = 2025;
    const currentYear = new Date().getFullYear();
    const maxYear = Math.max(currentYear, startYear);
    const years = [];
    for (let y = maxYear; y >= startYear; y--) {
      years.push(y);
    }
    return years;
  }, []);

  if (!stats) return null;

  return (
    <Box sx={{ mb: 3.5 }}>
      {/* Right-Aligned Period, Month & Year Filter directly above Statistics Cards */}
      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1.25} mb={2} flexWrap="wrap">
        {/* 1. Year Selector Dropdown Button (First) */}
        {statsYear && onYearChange && (
          <>
            <Button
              size="small"
              onClick={(e) => setYearAnchorEl(e.currentTarget)}
              endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#ffffff',
                color: '#0F3D3E',
                borderRadius: '20px',
                px: 1.75,
                py: 0.4,
                fontWeight: 700,
                fontSize: '0.78rem',
                textTransform: 'none',
                border: '1px solid #E2E8F0',
                boxShadow: '0 2px 6px rgba(15, 61, 62, 0.04)',
                height: 30,
                '&:hover': {
                  bgcolor: '#F1F5F9',
                  borderColor: '#0F3D3E',
                },
              }}
            >
              {statsYear}
            </Button>

            <Menu
              anchorEl={yearAnchorEl}
              open={Boolean(yearAnchorEl)}
              onClose={() => setYearAnchorEl(null)}
              PaperProps={{
                elevation: 6,
                sx: {
                  borderRadius: '14px',
                  mt: 1,
                  minWidth: 120,
                  boxShadow: '0 10px 30px rgba(15, 61, 62, 0.15)',
                  border: '1px solid #E2E8F0',
                  p: '4px',
                  '& .MuiMenuItem-root': {
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    py: 0.85,
                    px: 1.75,
                    borderRadius: '8px',
                    color: '#0F3D3E',
                    transition: 'all 0.15s ease',
                    '&.Mui-selected': {
                      bgcolor: '#0F3D3E',
                      color: '#ffffff',
                      fontWeight: 700,
                      '&:hover': {
                        bgcolor: '#165658',
                      },
                    },
                    '&:hover': {
                      bgcolor: '#F1F5F9',
                    },
                  },
                },
              }}
            >
              {availableYears.map((y) => (
                <MenuItem
                  key={y}
                  selected={statsYear === y}
                  onClick={() => {
                    onYearChange(y);
                    setYearAnchorEl(null);
                  }}
                >
                  {y}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        {/* 2. Separate Month Dropdown Button */}
        {onMonthChange && (
          <>
            <Button
              size="small"
              onClick={(e) => {
                if (statsPeriod !== 'month') onPeriodChange('month');
                setMonthAnchorEl(e.currentTarget);
              }}
              endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: statsPeriod === 'month' ? '#0F3D3E' : '#ffffff',
                color: statsPeriod === 'month' ? '#ffffff' : '#0F3D3E',
                borderRadius: '20px',
                px: 1.75,
                py: 0.4,
                fontWeight: 700,
                fontSize: '0.78rem',
                textTransform: 'none',
                border: '1px solid #E2E8F0',
                boxShadow: statsPeriod === 'month' ? '0 2px 6px rgba(15, 61, 62, 0.2)' : '0 2px 6px rgba(15, 61, 62, 0.04)',
                height: 30,
                '&:hover': {
                  bgcolor: statsPeriod === 'month' ? '#165658' : '#F1F5F9',
                  borderColor: '#0F3D3E',
                },
              }}
            >
              {getMonthDisplayLabel(statsMonth, statsPeriod, t)}
            </Button>

            <Menu
              anchorEl={monthAnchorEl}
              open={Boolean(monthAnchorEl)}
              onClose={() => setMonthAnchorEl(null)}
              PaperProps={{
                elevation: 6,
                sx: {
                  borderRadius: '14px',
                  mt: 1,
                  minWidth: 170,
                  maxHeight: 320,
                  boxShadow: '0 10px 30px rgba(15, 61, 62, 0.15)',
                  border: '1px solid #E2E8F0',
                  p: '4px',
                  '& .MuiMenuItem-root': {
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    py: 0.85,
                    px: 1.75,
                    borderRadius: '8px',
                    color: '#0F3D3E',
                    transition: 'all 0.15s ease',
                    '&.Mui-selected': {
                      bgcolor: '#0F3D3E',
                      color: '#ffffff',
                      fontWeight: 700,
                      '&:hover': {
                        bgcolor: '#165658',
                      },
                    },
                    '&:hover': {
                      bgcolor: '#F1F5F9',
                    },
                  },
                },
              }}
            >
              {MONTHS_OPTIONS.map((m) => (
                <MenuItem
                  key={m.value}
                  selected={statsPeriod === 'month' && statsMonth === m.value}
                  onClick={() => {
                    onMonthChange(m.value);
                    if (statsPeriod !== 'month') onPeriodChange('month');
                    setMonthAnchorEl(null);
                  }}
                >
                  {t(m.labelKey) || m.defaultLabel}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        {/* 3. Separate Week Button */}
        <Button
          size="small"
          onClick={() => onPeriodChange('week')}
          sx={{
            bgcolor: statsPeriod === 'week' ? '#0F3D3E' : '#ffffff',
            color: statsPeriod === 'week' ? '#ffffff' : '#0F3D3E',
            borderRadius: '20px',
            px: 1.75,
            py: 0.4,
            fontWeight: 700,
            fontSize: '0.78rem',
            textTransform: 'none',
            border: '1px solid #E2E8F0',
            boxShadow: statsPeriod === 'week' ? '0 2px 6px rgba(15, 61, 62, 0.2)' : '0 2px 6px rgba(15, 61, 62, 0.04)',
            height: 30,
            '&:hover': {
              bgcolor: statsPeriod === 'week' ? '#165658' : '#F1F5F9',
              borderColor: '#0F3D3E',
            },
          }}
        >
          {t('userActivity.periodWeek') || 'Week'}
        </Button>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.statUser')}
            stats={stats.userStats}
            icon={<People sx={{ fontSize: 22 }} />}
            color="#0F3D3E"
            gradient="linear-gradient(135deg, #0F3D3E 0%, #165658 100%)"
            t={t}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.statCity')}
            stats={stats.cityStats}
            icon={<LocationCity sx={{ fontSize: 22 }} />}
            color="#0891B2"
            gradient="linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)"
            onClick={onOpenCityDialog}
            t={t}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.statEvent')}
            stats={stats.eventStats}
            icon={<Event sx={{ fontSize: 22 }} />}
            color="#6366F1"
            gradient="linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)"
            t={t}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.statPromotion')}
            stats={stats.promotionStats}
            icon={<LocalOffer sx={{ fontSize: 22 }} />}
            color="#D97706"
            gradient="linear-gradient(135deg, #D97706 0%, #F59E0B 100%)"
            t={t}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.statContact')}
            stats={stats.contactStats}
            icon={<ContactPhone sx={{ fontSize: 22 }} />}
            color="#059669"
            gradient="linear-gradient(135deg, #059669 0%, #10B981 100%)"
            t={t}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.statAccommodation')}
            stats={stats.accommodationStats}
            icon={<Apartment sx={{ fontSize: 22 }} />}
            color="#0284C7"
            gradient="linear-gradient(135deg, #0284C7 0%, #3B82F6 100%)"
            t={t}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

DashboardStatsCards.propTypes = {
  stats: PropTypes.object,
  statsPeriod: PropTypes.string.isRequired,
  statsMonth: PropTypes.string,
  statsYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onPeriodChange: PropTypes.func.isRequired,
  onMonthChange: PropTypes.func,
  onYearChange: PropTypes.func,
  t: PropTypes.func.isRequired,
  onOpenCityDialog: PropTypes.func.isRequired,
};

// State-of-the-Art Activity Overview Chart Card (API Connected)
function ActivityTrendChartCard({ stats, t }) {
  const currentMonthVal = String(new Date().getMonth() + 1);
  const currentYearVal = new Date().getFullYear();
  const [hoverIndex, setHoverIndex] = useState(null);
  const [period, setPeriod] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(currentMonthVal);
  const [selectedYear, setSelectedYear] = useState(currentYearVal);
  const [monthAnchorEl, setMonthAnchorEl] = useState(null);
  const [yearAnchorEl, setYearAnchorEl] = useState(null);
  const [visitorData, setVisitorData] = useState(null);
  const [loading, setLoading] = useState(false);

  const availableYears = useMemo(() => {
    const startYear = 2025;
    const currentYear = new Date().getFullYear();
    const maxYear = Math.max(currentYear, startYear);
    const years = [];
    for (let y = maxYear; y >= startYear; y--) {
      years.push(y);
    }
    return years;
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const periodParam = (period === 'month' && selectedMonth !== 'all') ? '' : period;
    const monthParam = (period === 'month' && selectedMonth !== 'all') ? selectedMonth : null;
    const yearParam = selectedYear ? String(selectedYear) : null;

    userActivityAPI
      .getVisitors(periodParam, '', null, monthParam, yearParam)
      .then((res) => {
        if (!isMounted) return;
        const payload = res?.data?.payload || res?.payload || res?.data || res;
        setVisitorData(payload);
      })
      .catch((err) => {
        console.error('Error fetching visitor API data for Activity Overview:', err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [period, selectedMonth, selectedYear]);

  const totalUniqueVisitors = visitorData?.totalUniqueVisitors ?? 0;
  const totalVisits = visitorData?.totalVisits ?? 0;
  const contactVisitors = visitorData?.contactVisitors ?? 0;
  const eventVisitors = visitorData?.eventVisitors ?? 0;
  const promotionVisitors = visitorData?.promotionVisitors ?? 0;
  const accomodationVisitors = visitorData?.accomodationVisitors ?? 0;

  const trendPoints = useMemo(() => {
    const rawTrend = visitorData?.trend;
    if (Array.isArray(rawTrend) && rawTrend.length > 0) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return rawTrend.map((pt, idx) => {
        let dateStr = pt.periodLabel;
        if (period === 'year' || period === 'thisYear') {
          if (dateStr && !isNaN(parseInt(dateStr, 10)) && parseInt(dateStr, 10) >= 1 && parseInt(dateStr, 10) <= 12) {
            const mIdx = parseInt(dateStr, 10) - 1;
            dateStr = monthNames[mIdx];
          } else if (pt.dayUtc) {
            const d = new Date(pt.dayUtc);
            if (!isNaN(d.getTime())) dateStr = monthNames[d.getMonth()];
          }
        } else if (period === 'month' || period === 'thisMonth') {
          if (pt.dayUtc) {
            const d = new Date(pt.dayUtc);
            if (!isNaN(d.getTime())) {
              dateStr = `${d.getDate()}`;
            }
          }
        } else if (period === 'week' || period === 'thisWeek') {
          if (!dateStr && pt.dayUtc) {
            const d = new Date(pt.dayUtc);
            if (!isNaN(d.getTime())) {
              dateStr = d.toLocaleString('en-US', { weekday: 'short' });
            }
          }
        }
        if (!dateStr) dateStr = `${idx + 1}`;

        // Extract real visitor counts from backend API response for each point
        const contacts = pt.contactVisitors ?? pt.contactVisitor ?? 0;
        const events = pt.eventVisitors ?? pt.eventsVisitor ?? 0;
        const promotions = pt.promotionVisitors ?? pt.promotionVisitor ?? 0;
        const accommodations = pt.accomodationVisitors ?? pt.accommodationVisitors ?? 0;

        return {
          label: `${dateStr}`,
          contacts,
          events,
          promotions,
          accommodations,
          visits: pt.visits || 0,
          unique: pt.uniqueVisitors || 0,
        };
      });
    }

    const labels = (period === 'day' || period === 'today')
      ? ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00']
      : (period === 'week' || period === 'thisWeek')
        ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
        : (period === 'year' || period === 'thisYear')
          ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          : ['W1', 'W2', 'W3', 'W4'];

    return labels.map((lbl, idx) => ({
      label: lbl,
      contacts: Math.round(contactVisitors * (0.2 + idx * 0.15)),
      events: Math.round(eventVisitors * (0.15 + idx * 0.12)),
      promotions: Math.round(promotionVisitors * (0.1 + idx * 0.1)),
      accommodations: Math.round(accomodationVisitors * (0.05 + idx * 0.08)),
      visits: Math.round(totalVisits * (0.1 + idx * 0.15)),
      unique: Math.round(totalUniqueVisitors * (0.1 + idx * 0.12)),
    }));
  }, [visitorData, period, contactVisitors, eventVisitors, promotionVisitors, accomodationVisitors, totalVisits, totalUniqueVisitors]);

  const svgWidth = 1000;
  const svgHeight = 280;
  const padding = { top: 30, right: 35, bottom: 45, left: 45 };

  const maxVal = useMemo(() => {
    const vals = trendPoints.flatMap((d) => [d.contacts, d.events, d.promotions, d.accommodations, d.visits]);
    const highest = Math.max(...vals, 5);
    return Math.ceil(highest / 10) * 10 || 20;
  }, [trendPoints]);

  const points = useMemo(() => {
    const w = svgWidth - padding.left - padding.right;
    const h = svgHeight - padding.top - padding.bottom;
    const count = trendPoints.length;

    return trendPoints.map((d, i) => {
      const x = padding.left + (i * w) / (count - 1 || 1);
      const yContacts = padding.top + h - (d.contacts / maxVal) * h;
      const yEvents = padding.top + h - (d.events / maxVal) * h;
      const yPromotions = padding.top + h - (d.promotions / maxVal) * h;
      const yAccommodations = padding.top + h - (d.accommodations / maxVal) * h;
      return { ...d, x, yContacts, yEvents, yPromotions, yAccommodations, index: i };
    });
  }, [trendPoints, maxVal]);

  const makePath = (key) => {
    if (points.length === 0) return '';
    let str = `M ${points[0].x} ${points[0][key]}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cx1 = prev.x + (curr.x - prev.x) / 2;
      const cy1 = prev[key];
      const cx2 = prev.x + (curr.x - prev.x) / 2;
      const cy2 = curr[key];
      str += ` C ${cx1} ${cy1}, ${cx2} ${cy2}, ${curr.x} ${curr[key]}`;
    }
    return str;
  };

  const contactsPath = makePath('yContacts');
  const eventsPath = makePath('yEvents');
  const promotionsPath = makePath('yPromotions');
  const accommodationsPath = makePath('yAccommodations');

  const contactsArea = points.length > 0
    ? `${contactsPath} L ${points[points.length - 1].x} ${svgHeight - padding.bottom} L ${points[0].x} ${svgHeight - padding.bottom} Z`
    : '';

  return (
    <Card
      sx={{
        borderRadius: '16px',
        bgcolor: '#ffffff',
        border: '1px solid #E2E8F0',
        boxShadow: '0 2px 12px rgba(15, 61, 62, 0.04)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar sx={{ bgcolor: '#EBF4F4', color: '#0F3D3E', width: 34, height: 34 }}>
              <ShowChart sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F3D3E', fontSize: '1.02rem', lineHeight: 1.2 }}>
                {t('dashboard.activityOverview')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                {t('dashboard.activityOverviewSubtitle')}
              </Typography>
            </Box>
          </Box>
        }
        action={
          <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
            <Chip
              label={`${t('dashboard.totalUserVisits') || 'Total User Visits'}: ${totalVisits}`}
              size="small"
              sx={{
                bgcolor: '#E0F2FE',
                color: '#0369A1',
                fontWeight: 800,
                fontSize: '0.75rem',
                border: '1px solid #BAE6FD',
                height: 30,
                px: 0.5,
              }}
            />
            {/* Year Selector Dropdown Button (First) */}
            <Button
              size="small"
              onClick={(e) => setYearAnchorEl(e.currentTarget)}
              endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#ffffff',
                color: '#0F3D3E',
                borderRadius: '20px',
                px: 1.75,
                py: 0.4,
                fontWeight: 700,
                fontSize: '0.78rem',
                textTransform: 'none',
                border: '1px solid #E2E8F0',
                boxShadow: '0 2px 6px rgba(15, 61, 62, 0.04)',
                height: 30,
                '&:hover': {
                  bgcolor: '#F1F5F9',
                  borderColor: '#0F3D3E',
                },
              }}
            >
              {selectedYear}
            </Button>

            <Menu
              anchorEl={yearAnchorEl}
              open={Boolean(yearAnchorEl)}
              onClose={() => setYearAnchorEl(null)}
              PaperProps={{
                elevation: 6,
                sx: {
                  borderRadius: '14px',
                  mt: 1,
                  minWidth: 120,
                  boxShadow: '0 10px 30px rgba(15, 61, 62, 0.15)',
                  border: '1px solid #E2E8F0',
                  p: '4px',
                  '& .MuiMenuItem-root': {
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    py: 0.85,
                    px: 1.75,
                    borderRadius: '8px',
                    color: '#0F3D3E',
                    transition: 'all 0.15s ease',
                    '&.Mui-selected': {
                      bgcolor: '#0F3D3E',
                      color: '#ffffff',
                      fontWeight: 700,
                      '&:hover': {
                        bgcolor: '#165658',
                      },
                    },
                    '&:hover': {
                      bgcolor: '#F1F5F9',
                    },
                  },
                },
              }}
            >
              {availableYears.map((y) => (
                <MenuItem
                  key={y}
                  selected={selectedYear === y}
                  onClick={() => {
                    setSelectedYear(y);
                    setYearAnchorEl(null);
                  }}
                >
                  {y}
                </MenuItem>
              ))}
            </Menu>

            {/* Separate Month Dropdown Button */}
            <Button
              size="small"
              onClick={(e) => {
                if (period !== 'month') setPeriod('month');
                setMonthAnchorEl(e.currentTarget);
              }}
              endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: period === 'month' ? '#0F3D3E' : '#ffffff',
                color: period === 'month' ? '#ffffff' : '#0F3D3E',
                borderRadius: '20px',
                px: 1.75,
                py: 0.4,
                fontWeight: 700,
                fontSize: '0.78rem',
                textTransform: 'none',
                border: '1px solid #E2E8F0',
                boxShadow: period === 'month' ? '0 2px 6px rgba(15, 61, 62, 0.2)' : '0 2px 6px rgba(15, 61, 62, 0.04)',
                height: 30,
                '&:hover': {
                  bgcolor: period === 'month' ? '#165658' : '#F1F5F9',
                  borderColor: '#0F3D3E',
                },
              }}
            >
              {getMonthDisplayLabel(selectedMonth, period, t)}
            </Button>

            {/* Separate Week Button */}
            <Button
              size="small"
              onClick={() => setPeriod('week')}
              sx={{
                bgcolor: period === 'week' ? '#0F3D3E' : '#ffffff',
                color: period === 'week' ? '#ffffff' : '#0F3D3E',
                borderRadius: '20px',
                px: 1.75,
                py: 0.4,
                fontWeight: 700,
                fontSize: '0.78rem',
                textTransform: 'none',
                border: '1px solid #E2E8F0',
                boxShadow: period === 'week' ? '0 2px 6px rgba(15, 61, 62, 0.2)' : '0 2px 6px rgba(15, 61, 62, 0.04)',
                height: 30,
                '&:hover': {
                  bgcolor: period === 'week' ? '#165658' : '#F1F5F9',
                  borderColor: '#0F3D3E',
                },
              }}
            >
              {t('userActivity.periodWeek') || 'Week'}
            </Button>

            <Menu
              anchorEl={monthAnchorEl}
              open={Boolean(monthAnchorEl)}
              onClose={() => setMonthAnchorEl(null)}
              PaperProps={{
                elevation: 6,
                sx: {
                  borderRadius: '14px',
                  mt: 1,
                  minWidth: 170,
                  maxHeight: 320,
                  boxShadow: '0 10px 30px rgba(15, 61, 62, 0.15)',
                  border: '1px solid #E2E8F0',
                  p: '4px',
                  '& .MuiMenuItem-root': {
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    py: 0.85,
                    px: 1.75,
                    borderRadius: '8px',
                    color: '#0F3D3E',
                    transition: 'all 0.15s ease',
                    '&.Mui-selected': {
                      bgcolor: '#0F3D3E',
                      color: '#ffffff',
                      fontWeight: 700,
                      '&:hover': {
                        bgcolor: '#165658',
                      },
                    },
                    '&:hover': {
                      bgcolor: '#F1F5F9',
                    },
                  },
                },
              }}
            >
              {MONTHS_OPTIONS.map((m) => (
                <MenuItem
                  key={m.value}
                  selected={period === 'month' && selectedMonth === m.value}
                  onClick={() => {
                    setSelectedMonth(m.value);
                    if (period !== 'month') setPeriod('month');
                    setMonthAnchorEl(null);
                  }}
                >
                  {t(m.labelKey) || m.defaultLabel}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        }
        sx={{ p: 2.25, pb: 1.5 }}
      />
      <Divider />
      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box sx={{ width: '100%', position: 'relative', flex: 1 }}>
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            width="100%"
            height="100%"
            style={{ overflow: 'visible', display: 'block', cursor: 'crosshair', minHeight: 250 }}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const mouseX = ((e.clientX - rect.left) / rect.width) * svgWidth;
              let closest = 0;
              let minDiff = Infinity;
              points.forEach((pt, idx) => {
                const diff = Math.abs(pt.x - mouseX);
                if (diff < minDiff) {
                  minDiff = diff;
                  closest = idx;
                }
              });
              setHoverIndex(closest);
            }}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <defs>
              <linearGradient id="mainTealGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0F3D3E" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#0F3D3E" stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {/* Horizontal Grid lines & Y-Axis values */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
              const y = padding.top + (svgHeight - padding.top - padding.bottom) * ratio;
              const val = Math.round(maxVal * (1 - ratio));
              return (
                <g key={idx}>
                  <line x1={padding.left} y1={y} x2={svgWidth - padding.right} y2={y} stroke="#E2E8F0" strokeDasharray="4 4" />
                  <text x={padding.left - 10} y={y + 4} textAnchor="end" fontSize="11" fill="#64748B" fontWeight="600">
                    {val}
                  </text>
                </g>
              );
            })}

            {/* X-Axis Period Labels */}
            {points.map((pt, idx) => (
              <text key={idx} x={pt.x} y={svgHeight - 12} textAnchor="middle" fontSize="11.5" fill="#475569" fontWeight="700">
                {pt.label}
              </text>
            ))}

            {/* Area Fill for Contacts */}
            <path d={contactsArea} fill="url(#mainTealGradient)" />

            {/* Category Trend Line Curves */}
            <path d={contactsPath} fill="none" stroke="#0F3D3E" strokeWidth="3" strokeLinecap="round" />
            <path d={eventsPath} fill="none" stroke="#0891B2" strokeWidth="3" strokeLinecap="round" />
            <path d={promotionsPath} fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round" />
            <path d={accommodationsPath} fill="none" stroke="#165658" strokeWidth="3" strokeLinecap="round" />

            {/* Data Point Circles */}
            {points.map((pt, idx) => (
              <g key={idx}>
                <circle cx={pt.x} cy={pt.yContacts} r="3.5" fill="#0F3D3E" stroke="#ffffff" strokeWidth="1.5" />
                <circle cx={pt.x} cy={pt.yEvents} r="3.5" fill="#0891B2" stroke="#ffffff" strokeWidth="1.5" />
                <circle cx={pt.x} cy={pt.yPromotions} r="3.5" fill="#059669" stroke="#ffffff" strokeWidth="1.5" />
                <circle cx={pt.x} cy={pt.yAccommodations} r="3.5" fill="#165658" stroke="#ffffff" strokeWidth="1.5" />
              </g>
            ))}

            {/* Hover Guide Line */}
            {hoverIndex !== null && points[hoverIndex] && (
              <g>
                <line
                  x1={points[hoverIndex].x}
                  y1={padding.top}
                  x2={points[hoverIndex].x}
                  y2={svgHeight - padding.bottom}
                  stroke="#0F3D3E"
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                />
                <circle cx={points[hoverIndex].x} cy={points[hoverIndex].yContacts} r="6.5" fill="#0F3D3E" stroke="#ffffff" strokeWidth="2" />
                <circle cx={points[hoverIndex].x} cy={points[hoverIndex].yEvents} r="6.5" fill="#0891B2" stroke="#ffffff" strokeWidth="2" />
                <circle cx={points[hoverIndex].x} cy={points[hoverIndex].yPromotions} r="6.5" fill="#059669" stroke="#ffffff" strokeWidth="2" />
                <circle cx={points[hoverIndex].x} cy={points[hoverIndex].yAccommodations} r="6.5" fill="#165658" stroke="#ffffff" strokeWidth="2" />
              </g>
            )}
          </svg>

          {/* Floating Dark Hover Tooltip */}
          {hoverIndex !== null && points[hoverIndex] && (
            <Paper
              elevation={6}
              sx={{
                position: 'absolute',
                top: 15,
                left: Math.min(Math.max(points[hoverIndex].x - 75, 45), svgWidth - 170),
                p: 1.5,
                bgcolor: '#0F3D3E',
                color: '#ffffff',
                borderRadius: '10px',
                pointerEvents: 'none',
                zIndex: 10,
                boxShadow: '0 8px 20px rgba(15, 61, 62, 0.35)',
                minWidth: 150,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#EBF4F4', display: 'block', mb: 0.75, borderBottom: '1px solid rgba(255,255,255,0.15)', pb: 0.5 }}>
                {points[hoverIndex].label} {t('dashboard.activityMetrics')}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={0.35}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#0F3D3E' }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {t('dashboard.contactVisitors')}: <span style={{ color: '#99F6E4' }}>{points[hoverIndex].contacts}</span>
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.35}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#0891B2' }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {t('dashboard.eventVisitors')}: <span style={{ color: '#67E8F9' }}>{points[hoverIndex].events}</span>
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.35}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#059669' }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {t('dashboard.promotionVisitors')}: <span style={{ color: '#A7F3D0' }}>{points[hoverIndex].promotions}</span>
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#165658' }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {t('dashboard.accommodationVisitors')}: <span style={{ color: '#CBD5E1' }}>{points[hoverIndex].accommodations}</span>
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>

        {/* Category Breakdown Graph Legend */}
        <Box display="flex" justifyContent="center" gap={3} mt={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 14, height: 4, bgcolor: '#0F3D3E', borderRadius: 2 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
              {t('dashboard.contactVisitors')} ({contactVisitors})
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 14, height: 4, bgcolor: '#0891B2', borderRadius: 2 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#0891B2' }}>
              {t('dashboard.eventVisitors')} ({eventVisitors})
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 14, height: 4, bgcolor: '#059669', borderRadius: 2 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#059669' }}>
              {t('dashboard.promotionVisitors')} ({promotionVisitors})
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 14, height: 4, bgcolor: '#165658', borderRadius: 2 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#165658' }}>
              {t('dashboard.accommodationVisitors')} ({accomodationVisitors})
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

ActivityTrendChartCard.propTypes = {
  stats: PropTypes.object,
  t: PropTypes.func.isRequired,
};

// User Visits & Platform Traffic Chart Card (API Connected)
function PlatformUserVisitsChartCard({ stats, t }) {
  const currentMonthVal = String(new Date().getMonth() + 1);
  const [period, setPeriod] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(currentMonthVal);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [monthAnchorEl, setMonthAnchorEl] = useState(null);
  const [yearAnchorEl, setYearAnchorEl] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [visitorData, setVisitorData] = useState(null);
  const [loading, setLoading] = useState(false);

  const availableYears = useMemo(() => {
    const startYear = 2025;
    const currentYear = new Date().getFullYear();
    const maxYear = Math.max(currentYear, startYear);
    const years = [];
    for (let y = maxYear; y >= startYear; y--) {
      years.push(y);
    }
    return years;
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const periodParam = (period === 'month' && selectedMonth !== 'all') ? '' : period;
    const monthParam = (period === 'month' && selectedMonth !== 'all') ? selectedMonth : null;
    const yearParam = selectedYear ? String(selectedYear) : null;

    userActivityAPI
      .getVisitors(periodParam, '', null, monthParam, yearParam)
      .then((res) => {
        if (!isMounted) return;
        const payload = res?.data?.payload || res?.payload || res?.data || res;
        setVisitorData(payload);
      })
      .catch((err) => {
        console.error('Error fetching visitor API data for Platform User Visits:', err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [period, selectedMonth, selectedYear]);

  const totalUniqueVisitors = useMemo(() => {
    if (visitorData?.totalUniqueVisitors != null) return visitorData.totalUniqueVisitors;
    if (Array.isArray(visitorData?.trend)) {
      return visitorData.trend.reduce((acc, curr) => acc + (curr.uniqueVisitors || 0), 0);
    }
    return 0;
  }, [visitorData]);

  const androidVisitors = visitorData?.androidVisitors ?? 0;
  const iosVisitors = visitorData?.iosVisitors ?? 0;

  const visitsPointsData = useMemo(() => {
    const rawTrend = visitorData?.trend;
    if (Array.isArray(rawTrend) && rawTrend.length > 0) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return rawTrend.map((pt, idx) => {
        let label = pt.periodLabel || `${idx + 1}`;
        if (period === 'year' || period === 'thisYear') {
          if (pt.periodLabel && !isNaN(parseInt(pt.periodLabel, 10))) {
            const mIdx = parseInt(pt.periodLabel, 10) - 1;
            if (mIdx >= 0 && mIdx < 12) label = monthNames[mIdx];
          } else if (pt.dayUtc) {
            const d = new Date(pt.dayUtc);
            if (!isNaN(d.getTime())) label = monthNames[d.getMonth()];
          } else {
            label = monthNames[idx % 12];
          }
        } else if (period === 'month' || period === 'thisMonth') {
          if (pt.dayUtc) {
            const d = new Date(pt.dayUtc);
            if (!isNaN(d.getTime())) label = `${d.getDate()}`;
          }
        } else if (period === 'week' || period === 'thisWeek') {
          if (!label && pt.dayUtc) {
            const d = new Date(pt.dayUtc);
            if (!isNaN(d.getTime())) label = d.toLocaleString('en-US', { weekday: 'short' });
          }
        }

        return {
          date: label,
          android: pt.androidVisitors || 0,
          ios: pt.iosVisitors || 0,
        };
      });
    }

    const fallbackLabels = (period === 'year' || period === 'thisYear')
      ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      : (period === 'week' || period === 'thisWeek')
        ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
        : ['1', '5', '10', '15', '20', '25', '30'];

    return fallbackLabels.map((lbl, idx) => ({
      date: lbl,
      android: Math.round(androidVisitors * (0.15 + (idx % 5) * 0.1)),
      ios: Math.round(iosVisitors * (0.15 + (idx % 5) * 0.08)),
    }));
  }, [visitorData, androidVisitors, iosVisitors, period]);

  const svgWidth = 750;
  const svgHeight = 280;
  const padding = { top: 25, right: 30, bottom: 45, left: 35 };

  const maxVal = useMemo(() => {
    const vals = visitsPointsData.flatMap((d) => [d.android, d.ios]);
    const highest = Math.max(...vals, 10);
    return Math.ceil(highest / 10) * 10;
  }, [visitsPointsData]);

  const points = useMemo(() => {
    const w = svgWidth - padding.left - padding.right;
    const h = svgHeight - padding.top - padding.bottom;
    const count = visitsPointsData.length;

    return visitsPointsData.map((d, i) => {
      const x = padding.left + (i * w) / (count - 1 || 1);
      const yAndroid = padding.top + h - (d.android / maxVal) * h;
      const yIos = padding.top + h - (d.ios / maxVal) * h;
      return { ...d, x, yAndroid, yIos, index: i };
    });
  }, [visitsPointsData, maxVal]);

  const makeLinePath = (key) => {
    if (points.length === 0) return '';
    let str = `M ${points[0].x} ${points[0][key]}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cx1 = prev.x + (curr.x - prev.x) / 2;
      const cy1 = prev[key];
      const cx2 = prev.x + (curr.x - prev.x) / 2;
      const cy2 = curr[key];
      str += ` C ${cx1} ${cy1}, ${cx2} ${cy2}, ${curr.x} ${curr[key]}`;
    }
    return str;
  };

  const androidPath = makeLinePath('yAndroid');
  const iosPath = makeLinePath('yIos');

  const androidArea = points.length > 0
    ? `${androidPath} L ${points[points.length - 1].x} ${svgHeight - padding.bottom} L ${points[0].x} ${svgHeight - padding.bottom} Z`
    : '';

  return (
    <Card
      sx={{
        borderRadius: '16px',
        bgcolor: '#ffffff',
        border: '1px solid #E2E8F0',
        boxShadow: '0 2px 12px rgba(15, 61, 62, 0.04)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {/* Top Header Row */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1.5}>
          <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar sx={{ bgcolor: '#EBF4F4', color: '#0F3D3E', width: 38, height: 38, borderRadius: '10px' }}>
              <ShowChart sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F3D3E', fontSize: '1.05rem', lineHeight: 1.2 }}>
                {t('dashboard.platformUserVisitsTitle')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, fontSize: '0.76rem', mt: 0.25, display: 'block' }}>
                {t('dashboard.platformUserVisitsSubtitle')}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            {/* Total Unique Visitors on Right Side */}
            <Chip
              label={`${t('dashboard.totalUniqueVisitors') || 'Total Unique Visitors'}: ${totalUniqueVisitors}`}
              size="small"
              sx={{
                bgcolor: '#ECFDF5',
                color: '#059669',
                border: '1px solid #A7F3D0',
                fontWeight: 800,
                fontSize: '0.78rem',
                height: 32,
                px: 1,
              }}
            />

            {/* Year Selector Dropdown Button (First) */}
            <Button
              size="small"
              onClick={(e) => setYearAnchorEl(e.currentTarget)}
              endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#ffffff',
                color: '#0F3D3E',
                borderRadius: '20px',
                px: 1.75,
                py: 0.4,
                fontWeight: 700,
                fontSize: '0.78rem',
                textTransform: 'none',
                border: '1px solid #E2E8F0',
                boxShadow: '0 2px 6px rgba(15, 61, 62, 0.04)',
                height: 30,
                '&:hover': {
                  bgcolor: '#F1F5F9',
                  borderColor: '#0F3D3E',
                },
              }}
            >
              {selectedYear}
            </Button>

            <Menu
              anchorEl={yearAnchorEl}
              open={Boolean(yearAnchorEl)}
              onClose={() => setYearAnchorEl(null)}
              PaperProps={{
                elevation: 6,
                sx: {
                  borderRadius: '14px',
                  mt: 1,
                  minWidth: 120,
                  boxShadow: '0 10px 30px rgba(15, 61, 62, 0.15)',
                  border: '1px solid #E2E8F0',
                  p: '4px',
                  '& .MuiMenuItem-root': {
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    py: 0.85,
                    px: 1.75,
                    borderRadius: '8px',
                    color: '#0F3D3E',
                    transition: 'all 0.15s ease',
                    '&.Mui-selected': {
                      bgcolor: '#0F3D3E',
                      color: '#ffffff',
                      fontWeight: 700,
                      '&:hover': {
                        bgcolor: '#165658',
                      },
                    },
                    '&:hover': {
                      bgcolor: '#F1F5F9',
                    },
                  },
                },
              }}
            >
              {availableYears.map((y) => (
                <MenuItem
                  key={y}
                  selected={selectedYear === y}
                  onClick={() => {
                    setSelectedYear(y);
                    setYearAnchorEl(null);
                  }}
                >
                  {y}
                </MenuItem>
              ))}
            </Menu>

            {/* Separate Month Dropdown Button */}
            <Button
              size="small"
              onClick={(e) => {
                if (period !== 'month') setPeriod('month');
                setMonthAnchorEl(e.currentTarget);
              }}
              endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: period === 'month' ? '#0F3D3E' : '#ffffff',
                color: period === 'month' ? '#ffffff' : '#0F3D3E',
                borderRadius: '20px',
                px: 1.75,
                py: 0.4,
                fontWeight: 700,
                fontSize: '0.78rem',
                textTransform: 'none',
                border: '1px solid #E2E8F0',
                boxShadow: period === 'month' ? '0 2px 6px rgba(15, 61, 62, 0.2)' : '0 2px 6px rgba(15, 61, 62, 0.04)',
                height: 30,
                '&:hover': {
                  bgcolor: period === 'month' ? '#165658' : '#F1F5F9',
                  borderColor: '#0F3D3E',
                },
              }}
            >
              {getMonthDisplayLabel(selectedMonth, period, t)}
            </Button>

            <Menu
              anchorEl={monthAnchorEl}
              open={Boolean(monthAnchorEl)}
              onClose={() => setMonthAnchorEl(null)}
              PaperProps={{
                elevation: 6,
                sx: {
                  borderRadius: '14px',
                  mt: 1,
                  minWidth: 170,
                  maxHeight: 320,
                  boxShadow: '0 10px 30px rgba(15, 61, 62, 0.15)',
                  border: '1px solid #E2E8F0',
                  p: '4px',
                  '& .MuiMenuItem-root': {
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    py: 0.85,
                    px: 1.75,
                    borderRadius: '8px',
                    color: '#0F3D3E',
                    transition: 'all 0.15s ease',
                    '&.Mui-selected': {
                      bgcolor: '#0F3D3E',
                      color: '#ffffff',
                      fontWeight: 700,
                      '&:hover': {
                        bgcolor: '#165658',
                      },
                    },
                    '&:hover': {
                      bgcolor: '#F1F5F9',
                    },
                  },
                },
              }}
            >
              {MONTHS_OPTIONS.map((m) => (
                <MenuItem
                  key={m.value}
                  selected={period === 'month' && selectedMonth === m.value}
                  onClick={() => {
                    setSelectedMonth(m.value);
                    if (period !== 'month') setPeriod('month');
                    setMonthAnchorEl(null);
                  }}
                >
                  {t(m.labelKey) || m.defaultLabel}
                </MenuItem>
              ))}
            </Menu>

            {/* Separate Week Button */}
            <Button
              size="small"
              onClick={() => setPeriod('week')}
              sx={{
                bgcolor: period === 'week' ? '#0F3D3E' : '#ffffff',
                color: period === 'week' ? '#ffffff' : '#0F3D3E',
                borderRadius: '20px',
                px: 1.75,
                py: 0.4,
                fontWeight: 700,
                fontSize: '0.78rem',
                textTransform: 'none',
                border: '1px solid #E2E8F0',
                boxShadow: period === 'week' ? '0 2px 6px rgba(15, 61, 62, 0.2)' : '0 2px 6px rgba(15, 61, 62, 0.04)',
                height: 30,
                '&:hover': {
                  bgcolor: period === 'week' ? '#165658' : '#F1F5F9',
                  borderColor: '#0F3D3E',
                },
              }}
            >
              {t('userActivity.periodWeek') || 'Week'}
            </Button>
          </Box>
        </Box>

        {/* SVG Line Graph (2 Lines: Android & iOS) */}
        <Box sx={{ width: '100%', position: 'relative', flex: 1, my: 1, display: 'flex', alignItems: 'center' }}>
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            width="100%"
            height="100%"
            style={{ overflow: 'visible', display: 'block', cursor: 'crosshair', minHeight: 250 }}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const mouseX = ((e.clientX - rect.left) / rect.width) * svgWidth;
              let closest = 0;
              let minDiff = Infinity;
              points.forEach((pt, idx) => {
                const diff = Math.abs(pt.x - mouseX);
                if (diff < minDiff) {
                  minDiff = diff;
                  closest = idx;
                }
              });
              setHoverIndex(closest);
            }}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <defs>
              <linearGradient id="androidAreaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0891B2" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#0891B2" stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {/* Grid lines & Y-Axis values */}
            {[0, 0.33, 0.66, 1].map((ratio, idx) => {
              const y = padding.top + (svgHeight - padding.top - padding.bottom) * ratio;
              const val = Math.round(maxVal * (1 - ratio));
              return (
                <g key={idx}>
                  <line x1={padding.left} y1={y} x2={svgWidth - padding.right} y2={y} stroke="#E2E8F0" strokeDasharray="4 4" />
                  <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#94A3B8" fontWeight="600">
                    {val}
                  </text>
                </g>
              );
            })}

            {/* X-Axis Date Labels */}
            {points.map((pt, idx) => (
              <text key={idx} x={pt.x} y={svgHeight - 12} textAnchor="middle" fontSize="11" fill="#475569" fontWeight="700">
                {pt.date}
              </text>
            ))}

            {/* Gradient Area Fill for Android */}
            <path d={androidArea} fill="url(#androidAreaGrad)" />

            {/* 2 Line Curves: Android (Cyan) & iOS (Dark Teal / Slate) */}
            <path d={androidPath} fill="none" stroke="#0891B2" strokeWidth="3" strokeLinecap="round" />
            <path d={iosPath} fill="none" stroke="#0F3D3E" strokeWidth="2.5" strokeDasharray="4 3" strokeLinecap="round" />

            {/* Interactive Circles for Android & iOS */}
            {points.map((pt, idx) => (
              <g key={idx}>
                <circle cx={pt.x} cy={pt.yAndroid} r="4" fill="#0891B2" stroke="#ffffff" strokeWidth="1.5" />
                <circle cx={pt.x} cy={pt.yIos} r="3.5" fill="#0F3D3E" stroke="#ffffff" strokeWidth="1.5" />
              </g>
            ))}

            {/* Hover Vertical Guide Line */}
            {hoverIndex !== null && points[hoverIndex] && (
              <g>
                <line
                  x1={points[hoverIndex].x}
                  y1={padding.top}
                  x2={points[hoverIndex].x}
                  y2={svgHeight - padding.bottom}
                  stroke="#0F3D3E"
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                />
                <circle cx={points[hoverIndex].x} cy={points[hoverIndex].yAndroid} r="6" fill="#0891B2" stroke="#ffffff" strokeWidth="2" />
                <circle cx={points[hoverIndex].x} cy={points[hoverIndex].yIos} r="6" fill="#0F3D3E" stroke="#ffffff" strokeWidth="2" />
              </g>
            )}
          </svg>

          {/* Floating Hover Tooltip Popup */}
          {hoverIndex !== null && points[hoverIndex] && (
            <Paper
              elevation={6}
              sx={{
                position: 'absolute',
                top: 10,
                left: Math.min(Math.max(points[hoverIndex].x - 65, 30), svgWidth - 150),
                p: 1.35,
                bgcolor: '#0F3D3E',
                color: '#ffffff',
                borderRadius: '10px',
                pointerEvents: 'none',
                zIndex: 10,
                boxShadow: '0 8px 24px rgba(15, 61, 62, 0.35)',
                minWidth: 130,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#EBF4F4', display: 'block', mb: 0.75, borderBottom: '1px solid rgba(255,255,255,0.15)', pb: 0.4 }}>
                Period: {points[hoverIndex].date}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#38BDF8', mt: 0.2 }}>
                Android: <span style={{ color: '#ffffff' }}>{points[hoverIndex].android}</span>
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#99F6E4', mt: 0.2 }}>
                iOS: <span style={{ color: '#ffffff' }}>{points[hoverIndex].ios}</span>
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Bottom Legend */}
        <Box display="flex" alignItems="center" justifyContent="space-between" pt={1.5} borderTop="1px solid #F1F5F9">
          <Box display="flex" alignItems="center" gap={2.5}>
            <Box display="flex" alignItems="center" gap={0.75}>
              <Box sx={{ width: 12, height: 3.5, bgcolor: '#0891B2', borderRadius: 1 }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#0891B2', fontSize: '0.78rem' }}>
                Android ({androidVisitors})
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.75}>
              <Box sx={{ width: 12, height: 3.5, bgcolor: '#0F3D3E', borderRadius: 1 }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#0F3D3E', fontSize: '0.78rem' }}>
                iOS ({iosVisitors})
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

PlatformUserVisitsChartCard.propTypes = {
  stats: PropTypes.object,
  t: PropTypes.func.isRequired,
};

// Category Donut Chart Card
function CategoryDonutChartCard({ categories, t }) {
  const [hoverIndex, setHoverIndex] = useState(null);

  const themeColors = ['#0F3D3E', '#0891B2', '#059669', '#165658', '#328284', '#0284C7', '#1E6B6D', '#14B8A6'];

  const totalCount = useMemo(() => {
    return categories.reduce((acc, c) => acc + (c.count || 0), 0);
  }, [categories]);

  const slices = useMemo(() => {
    if (!categories || categories.length === 0 || totalCount === 0) return [];
    let cumulativeAngle = 0;
    const center = 100;
    const radius = 68;

    return categories.slice(0, 10).map((cat, idx) => {
      const percentage = (cat.count / totalCount) * 100;
      const angle = (cat.count / totalCount) * 360;
      const startAngle = cumulativeAngle;
      cumulativeAngle += angle;

      const startRad = ((startAngle - 90) * Math.PI) / 180;
      const endRad = ((startAngle + angle - 90) * Math.PI) / 180;

      const x1 = center + radius * Math.cos(startRad);
      const y1 = center + radius * Math.sin(startRad);
      const x2 = center + radius * Math.cos(endRad);
      const y2 = center + radius * Math.sin(endRad);

      const largeArc = angle > 180 ? 1 : 0;
      const pathData = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;

      return {
        cat,
        percentage: percentage.toFixed(1),
        pathData,
        color: themeColors[idx % themeColors.length],
        index: idx,
      };
    });
  }, [categories, totalCount]);

  return (
    <Card
      sx={{
        borderRadius: '16px',
        bgcolor: '#ffffff',
        border: '1px solid #E2E8F0',
        boxShadow: '0 2px 12px rgba(15, 61, 62, 0.04)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar sx={{ bgcolor: '#EBF4F4', color: '#0F3D3E', width: 34, height: 34 }}>
              <PieChart sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F3D3E', fontSize: '1.02rem' }}>
              {t('dashboard.categoryBreakdown')}
            </Typography>
          </Box>
        }
        sx={{ p: 2.25, pb: 1.5 }}
      />
      <Divider />
      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {categories.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {t('dashboard.noCategoryData')}
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center">
            {/* Donut Graphic */}
            <Box sx={{ position: 'relative', width: 190, height: 190, my: 0.5 }}>
              <svg width="190" height="190" viewBox="0 0 200 200" style={{ overflow: 'visible' }}>
                {slices.map((slice) => (
                  <path
                    key={slice.index}
                    d={slice.pathData}
                    fill="none"
                    stroke={slice.color}
                    strokeWidth={hoverIndex === slice.index ? 28 : 22}
                    strokeLinecap="round"
                    style={{ transition: 'all 0.25s ease', cursor: 'pointer' }}
                    onMouseEnter={() => setHoverIndex(slice.index)}
                    onMouseLeave={() => setHoverIndex(null)}
                  />
                ))}
              </svg>
              {/* Donut Center Label */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 900, color: '#0F3D3E', lineHeight: 1 }}>
                  {hoverIndex !== null && slices[hoverIndex] ? slices[hoverIndex].cat.count : totalCount}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.68rem', fontWeight: 700, display: 'block', mt: 0.25 }}>
                  {hoverIndex !== null && slices[hoverIndex] ? slices[hoverIndex].cat.categoryName : t('dashboard.totalItems')}
                </Typography>
              </Box>
            </Box>

            {/* Scrollable Legend */}
            <Box sx={{ width: '100%', mt: 1, maxHeight: 140, overflowY: 'auto' }}>
              {slices.map((slice) => (
                <Box
                  key={slice.index}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    p: 0.65,
                    borderRadius: '8px',
                    bgcolor: hoverIndex === slice.index ? '#EBF4F4' : 'transparent',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={() => setHoverIndex(slice.index)}
                  onMouseLeave={() => setHoverIndex(null)}
                >
                  <Box display="flex" alignItems="center" gap={1} sx={{ maxWidth: '68%' }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: slice.color, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontSize: '0.76rem' }} noWrap>
                      {slice.cat.categoryName}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#0F3D3E' }}>
                      {slice.cat.count}
                    </Typography>
                    <Chip label={`${slice.percentage}%`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#F8FAFC' }} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

CategoryDonutChartCard.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.object).isRequired,
  t: PropTypes.func.isRequired,
};

function ModuleComparisonChartCard({ moduleHealth: propModuleHealth, stats, t }) {
  const chartContainerRef = useRef(null);
  const [activeHoverBar, setActiveHoverBar] = useState(null);
  const [mousePos, setMousePos] = useState(null);
  const [fetchedHealth, setFetchedHealth] = useState(null);

  // If not passed from parent, fetch independently
  useEffect(() => {
    if (propModuleHealth) return;
    let isMounted = true;
    dashboardAPI.getModuleHealth()
      .then((res) => {
        if (isMounted && res.data?.success && res.data?.payload) {
          setFetchedHealth(res.data.payload);
        }
      })
      .catch((err) => {
        console.error('Error fetching module health:', err);
      });
    return () => {
      isMounted = false;
    };
  }, [propModuleHealth]);

  const activeHealth = propModuleHealth || fetchedHealth;

  const handleBarHover = (e, barData) => {
    if (chartContainerRef.current) {
      const rect = chartContainerRef.current.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
    setActiveHoverBar(barData);
  };

  const handleBarMouseMove = (e) => {
    if (chartContainerRef.current) {
      const rect = chartContainerRef.current.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleBarLeave = () => {
    setActiveHoverBar(null);
    setMousePos(null);
  };

  const defaultSampleModules = useMemo(() => [
    {
      module: 'Events',
      total: 8,
      approved: 7,
      pending: 1,
      inactive: 0,
      rejected: 0,
      activeRatePercentage: 87.5,
      activeRate: '88% active',
    },
    {
      module: 'Promotions',
      total: 1,
      approved: 11,
      pending: 1,
      inactive: 0,
      rejected: 0,
      activeRatePercentage: 1100.0,
      activeRate: '1100% active',
    },
    {
      module: 'Contacts',
      total: 54,
      approved: 51,
      pending: 1,
      inactive: 2,
      rejected: 0,
      activeRatePercentage: 94.4,
      activeRate: '94% active',
    },
    {
      module: 'Accommodations',
      total: 2,
      approved: 1,
      pending: 1,
      inactive: 0,
      rejected: 0,
      activeRatePercentage: 50.0,
      activeRate: '50% active',
    },
  ], []);

  const getModuleIcon = (moduleName = '') => {
    const name = String(moduleName).toLowerCase();
    if (name.includes('event')) return <Event sx={{ fontSize: 15 }} />;
    if (name.includes('promotion')) return <LocalOffer sx={{ fontSize: 15 }} />;
    if (name.includes('contact')) return <ContactPhone sx={{ fontSize: 15 }} />;
    if (name.includes('accommodat')) return <Apartment sx={{ fontSize: 15 }} />;
    if (name.includes('city') || name.includes('cities')) return <LocationCity sx={{ fontSize: 15 }} />;
    if (name.includes('user')) return <People sx={{ fontSize: 15 }} />;
    return <BarChart sx={{ fontSize: 15 }} />;
  };

  const modulesData = useMemo(() => {
    if (Array.isArray(activeHealth?.modules) && activeHealth.modules.length > 0) {
      return activeHealth.modules.map((m) => {
        const name = m.module || m.name || '';
        const key = name.toLowerCase();
        const total = Number(m.total ?? 0);
        const approved = Number(m.approved ?? m.active ?? 0);
        const pending = Number(m.pending ?? 0);
        const inactive = Number(m.inactive ?? 0);
        const rejected = Number(m.rejected ?? 0);
        const activeRatePercentage = m.activeRatePercentage != null
          ? Number(m.activeRatePercentage)
          : (total > 0 ? (approved / total) * 100 : 0);
        const activeRate = m.activeRate || `${Math.round(activeRatePercentage)}% ${t('dashboard.activeLabel') || 'active'}`;
        return {
          name,
          key,
          icon: getModuleIcon(name),
          total,
          approved,
          pending,
          inactive,
          rejected,
          activeRatePercentage,
          activeRate,
        };
      });
    }

    if (stats) {
      return [
        {
          name: 'Events',
          key: 'events',
          icon: <Event sx={{ fontSize: 15 }} />,
          total: getStatTotal(stats?.eventStats) || 8,
          approved: getStatActive(stats?.eventStats) || 7,
          pending: getStatPending(stats?.eventStats) || 1,
          activeRate: '88% active',
        },
        {
          name: 'Promotions',
          key: 'promotions',
          icon: <LocalOffer sx={{ fontSize: 15 }} />,
          total: getStatTotal(stats?.promotionStats) || 1,
          approved: getStatActive(stats?.promotionStats) || 11,
          pending: getStatPending(stats?.promotionStats) || 1,
          activeRate: '1100% active',
        },
        {
          name: 'Contacts',
          key: 'contacts',
          icon: <ContactPhone sx={{ fontSize: 15 }} />,
          total: getStatTotal(stats?.contactStats) || 54,
          approved: getStatActive(stats?.contactStats) || 51,
          pending: getStatPending(stats?.contactStats) || 1,
          activeRate: '94% active',
        },
        {
          name: 'Accommodations',
          key: 'accommodations',
          icon: <Apartment sx={{ fontSize: 15 }} />,
          total: getStatTotal(stats?.accommodationStats) || 2,
          approved: getStatActive(stats?.accommodationStats) || 1,
          pending: getStatPending(stats?.accommodationStats) || 1,
          activeRate: '50% active',
        },
      ];
    }

    return defaultSampleModules.map((m) => ({
      name: m.module,
      key: m.module.toLowerCase(),
      icon: getModuleIcon(m.module),
      total: m.total,
      approved: m.approved,
      pending: m.pending,
      inactive: m.inactive,
      rejected: m.rejected,
      activeRatePercentage: m.activeRatePercentage,
      activeRate: m.activeRate,
    }));
  }, [activeHealth, stats, defaultSampleModules, t]);

  const summary = useMemo(() => {
    if (activeHealth?.summary) {
      const s = activeHealth.summary;
      return {
        totalRecords: Number(s.totalRecords ?? 0),
        approvedEntities: Number(s.approvedEntities ?? 0),
        pendingApprovals: Number(s.pendingApprovals ?? 0),
        activeRatePercentage: s.activeRatePercentage != null ? Number(s.activeRatePercentage) : null,
        activeRate: s.activeRate || (s.activeRatePercentage != null ? `${Math.round(s.activeRatePercentage)}% ${t('dashboard.activeRate') || 'Active Rate'}` : '108% Active Rate'),
      };
    }
    const totalRecords = modulesData.reduce((acc, m) => acc + m.total, 0) || 65;
    const approvedEntities = modulesData.reduce((acc, m) => acc + m.approved, 0) || 70;
    const pendingApprovals = modulesData.reduce((acc, m) => acc + m.pending, 0) || 4;
    const activeRatePercentage = totalRecords > 0 ? (approvedEntities / totalRecords) * 100 : 0;
    const activeRate = `${Math.round(activeRatePercentage)}% ${t('dashboard.activeRate') || 'Active Rate'}`;
    return { totalRecords, approvedEntities, pendingApprovals, activeRatePercentage, activeRate };
  }, [activeHealth, modulesData, t]);

  const maxVal = useMemo(() => {
    const vals = modulesData.flatMap((m) => [m.total, m.approved, m.pending]);
    const summaryTotal = summary.totalRecords || 0;
    const highest = Math.max(...vals, summaryTotal, 10);
    return highest;
  }, [modulesData, summary]);

  const svgWidth = 600;
  const svgHeight = 350;
  const padding = { top: 35, right: 20, bottom: 50, left: 35 };
  const chartH = svgHeight - padding.top - padding.bottom;
  const chartW = svgWidth - padding.left - padding.right;
  const groupCount = modulesData.length || 1;
  const groupWidth = chartW / groupCount;
  const barW = 22;
  const gap = 6;
  const totalBarsWidth = barW * 3 + gap * 2;

  // 5 Ticks: max, 75%, 50%, 25%, 0
  const yTicks = [
    { ratio: 0, val: Math.round(maxVal) },
    { ratio: 0.25, val: Math.round(maxVal * 0.75) },
    { ratio: 0.5, val: Math.round(maxVal * 0.5) },
    { ratio: 0.75, val: Math.round(maxVal * 0.25) },
    { ratio: 1, val: 0 },
  ];

  // Tooltip coordinates dynamically tracking cursor
  const tooltipCoords = useMemo(() => {
    if (!mousePos || !chartContainerRef.current) {
      return null;
    }
    const containerW = chartContainerRef.current.clientWidth || 580;
    const containerH = chartContainerRef.current.clientHeight || 340;
    const tooltipW = 165;
    const tooltipH = 55;

    // Center horizontally over cursor, clamp within boundaries
    let left = mousePos.x - tooltipW / 2;
    if (left < 10) left = 10;
    if (left + tooltipW > containerW - 10) left = containerW - tooltipW - 10;

    // Position above cursor; if too close to top of card, flip below cursor
    let top = mousePos.y - tooltipH - 12;
    if (top < 8) {
      top = mousePos.y + 16;
    }
    if (top + tooltipH > containerH - 8) {
      top = containerH - tooltipH - 8;
    }

    return { left, top };
  }, [mousePos]);

  return (
    <Card
      sx={{
        borderRadius: '16px',
        bgcolor: '#ffffff',
        border: '1px solid #E2E8F0',
        boxShadow: '0 2px 12px rgba(15, 61, 62, 0.04)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <CardHeader
        title={
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.25}>
              <Avatar sx={{ bgcolor: '#F1F5F9', color: '#0F3D3E', width: 40, height: 40, borderRadius: '10px' }}>
                <BarChart sx={{ fontSize: 24, color: '#0F3D3E' }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F3D3E', fontSize: '1.05rem', lineHeight: 1.2 }}>
                  {t('dashboard.moduleHealthTitle') || 'Module Health & Volume Comparison'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, fontSize: '0.82rem' }}>
                  {t('dashboard.moduleHealthSubtitle') || 'Real-time entity counts & operational status across system modules'}
                </Typography>
              </Box>
            </Box>

            <Chip
              icon={<TrendingUp sx={{ fontSize: '15px !important', color: '#059669 !important' }} />}
              label={summary.activeRate}
              size="small"
              sx={{
                bgcolor: '#ECFDF5',
                color: '#059669',
                fontWeight: 800,
                fontSize: '0.78rem',
                border: '1px solid #A7F3D0',
                borderRadius: '20px',
                height: 28,
                px: 0.5,
              }}
            />
          </Box>
        }
        sx={{ p: 2.5, pb: 1.75 }}
      />
      <Divider sx={{ borderColor: '#F1F5F9' }} />

      {/* Top Quick Stats Strip */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 130,
            p: 1.5,
            px: 2,
            borderRadius: '12px',
            bgcolor: '#ffffff',
            border: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box sx={{ width: 4, height: 26, borderRadius: '2px', bgcolor: '#0F3D3E' }} />
          <Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block', lineHeight: 1.2 }}>
              {t('dashboard.totalRecords') || 'Total Records'}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#0F3D3E', fontSize: '1.25rem', lineHeight: 1.2, mt: 0.25 }}>
              {summary.totalRecords}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 130,
            p: 1.5,
            px: 2,
            borderRadius: '12px',
            bgcolor: '#ffffff',
            border: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box sx={{ width: 4, height: 26, borderRadius: '2px', bgcolor: '#059669' }} />
          <Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block', lineHeight: 1.2 }}>
              {t('dashboard.activeEntities') || 'Approved Entities'}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#059669', fontSize: '1.25rem', lineHeight: 1.2, mt: 0.25 }}>
              {summary.approvedEntities}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 130,
            p: 1.5,
            px: 2,
            borderRadius: '12px',
            bgcolor: '#ffffff',
            border: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box sx={{ width: 4, height: 26, borderRadius: '2px', bgcolor: '#D97706' }} />
          <Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block', lineHeight: 1.2 }}>
              {t('dashboard.pendingApprovals') || 'Pending Approvals'}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#D97706', fontSize: '1.25rem', lineHeight: 1.2, mt: 0.25 }}>
              {summary.pendingApprovals}
            </Typography>
          </Box>
        </Box>
      </Box>

      <CardContent sx={{ px: 2, py: 1.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative' }}>
        <Box
          ref={chartContainerRef}
          sx={{ width: '100%', position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}
        >
          <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} width="100%" height="340" style={{ overflow: 'visible', display: 'block' }}>
            {/* Grid lines & Y-Axis Scale */}
            {yTicks.map((tick, idx) => {
              const y = padding.top + chartH * tick.ratio;
              return (
                <g key={idx}>
                  <line
                    x1={padding.left}
                    y1={y}
                    x2={svgWidth - padding.right}
                    y2={y}
                    stroke="#E2E8F0"
                    strokeDasharray="4 4"
                    strokeWidth="1"
                  />
                  <text
                    x={padding.left - 8}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="11"
                    fill="#94A3B8"
                    fontWeight="600"
                  >
                    {tick.val}
                  </text>
                </g>
              );
            })}

            {/* Bar Groups */}
            {modulesData.map((mod, i) => {
              const groupX = padding.left + i * groupWidth;
              const startX = groupX + (groupWidth - totalBarsWidth) / 2;

              const xTotal = startX;
              const xApproved = startX + barW + gap;
              const xPending = startX + (barW + gap) * 2;

              const hTotal = mod.total > 0 ? Math.max((mod.total / maxVal) * chartH, 4) : 0;
              const hApproved = mod.approved > 0 ? Math.max((mod.approved / maxVal) * chartH, 4) : 0;
              const hPending = mod.pending > 0 ? Math.max((mod.pending / maxVal) * chartH, 4) : 0;

              const yTotal = svgHeight - padding.bottom - hTotal;
              const yApproved = svgHeight - padding.bottom - hApproved;
              const yPending = svgHeight - padding.bottom - hPending;

              const displayName = t(`nav.${mod.key}`) || mod.name;

              return (
                <g key={mod.name || i}>
                  {/* Total Bar */}
                  {hTotal > 0 && (
                    <rect
                      x={xTotal}
                      y={yTotal}
                      width={barW}
                      height={hTotal}
                      rx="4"
                      fill="#0F3D3E"
                      opacity={activeHoverBar && activeHoverBar.key !== `${i}-total` ? 0.75 : 1}
                      onMouseEnter={(e) =>
                        handleBarHover(e, {
                          key: `${i}-total`,
                          moduleIndex: i,
                          moduleName: displayName,
                          series: t('dashboard.totalVolume') || 'Total Volume',
                          count: mod.total,
                        })
                      }
                      onMouseMove={handleBarMouseMove}
                      onMouseLeave={handleBarLeave}
                      style={{ transition: 'opacity 0.2s', cursor: 'pointer' }}
                    />
                  )}
                  {/* Value above Total Bar */}
                  <text
                    x={xTotal + barW / 2}
                    y={yTotal - 5}
                    textAnchor="middle"
                    fontSize="11"
                    fill="#0F3D3E"
                    fontWeight="800"
                    onMouseEnter={(e) =>
                      handleBarHover(e, {
                        key: `${i}-total`,
                        moduleIndex: i,
                        moduleName: displayName,
                        series: t('dashboard.totalVolume') || 'Total Volume',
                        count: mod.total,
                      })
                    }
                    onMouseMove={handleBarMouseMove}
                    onMouseLeave={handleBarLeave}
                    style={{ cursor: 'pointer' }}
                  >
                    {mod.total}
                  </text>

                  {/* Approved Bar */}
                  {hApproved > 0 && (
                    <rect
                      x={xApproved}
                      y={yApproved}
                      width={barW}
                      height={hApproved}
                      rx="4"
                      fill="#059669"
                      opacity={activeHoverBar && activeHoverBar.key !== `${i}-approved` ? 0.75 : 1}
                      onMouseEnter={(e) =>
                        handleBarHover(e, {
                          key: `${i}-approved`,
                          moduleIndex: i,
                          moduleName: displayName,
                          series: t('dashboard.activeEntities') || 'Approved Entities',
                          count: mod.approved,
                        })
                      }
                      onMouseMove={handleBarMouseMove}
                      onMouseLeave={handleBarLeave}
                      style={{ transition: 'opacity 0.2s', cursor: 'pointer' }}
                    />
                  )}
                  {/* Value above Approved Bar */}
                  <text
                    x={xApproved + barW / 2}
                    y={yApproved - 5}
                    textAnchor="middle"
                    fontSize="11"
                    fill="#059669"
                    fontWeight="800"
                    onMouseEnter={(e) =>
                      handleBarHover(e, {
                        key: `${i}-approved`,
                        moduleIndex: i,
                        moduleName: displayName,
                        series: t('dashboard.activeEntities') || 'Approved Entities',
                        count: mod.approved,
                      })
                    }
                    onMouseMove={handleBarMouseMove}
                    onMouseLeave={handleBarLeave}
                    style={{ cursor: 'pointer' }}
                  >
                    {mod.approved}
                  </text>

                  {/* Pending Bar */}
                  {hPending > 0 && (
                    <rect
                      x={xPending}
                      y={yPending}
                      width={barW}
                      height={hPending}
                      rx="4"
                      fill="#D97706"
                      opacity={activeHoverBar && activeHoverBar.key !== `${i}-pending` ? 0.75 : 1}
                      onMouseEnter={(e) =>
                        handleBarHover(e, {
                          key: `${i}-pending`,
                          moduleIndex: i,
                          moduleName: displayName,
                          series: t('dashboard.pendingApprovals') || 'Pending Approvals',
                          count: mod.pending,
                        })
                      }
                      onMouseMove={handleBarMouseMove}
                      onMouseLeave={handleBarLeave}
                      style={{ transition: 'opacity 0.2s', cursor: 'pointer' }}
                    />
                  )}
                  {/* Value above Pending Bar */}
                  <text
                    x={xPending + barW / 2}
                    y={yPending - 5}
                    textAnchor="middle"
                    fontSize="11"
                    fill="#D97706"
                    fontWeight="800"
                    onMouseEnter={(e) =>
                      handleBarHover(e, {
                        key: `${i}-pending`,
                        moduleIndex: i,
                        moduleName: displayName,
                        series: t('dashboard.pendingApprovals') || 'Pending Approvals',
                        count: mod.pending,
                      })
                    }
                    onMouseMove={handleBarMouseMove}
                    onMouseLeave={handleBarLeave}
                    style={{ cursor: 'pointer' }}
                  >
                    {mod.pending}
                  </text>

                  {/* Module Label & Active Rate below baseline */}
                  <text
                    x={groupX + groupWidth / 2}
                    y={svgHeight - 25}
                    textAnchor="middle"
                    fontSize="12.5"
                    fill="#0F172A"
                    fontWeight="800"
                  >
                    {displayName}
                  </text>
                  <text
                    x={groupX + groupWidth / 2}
                    y={svgHeight - 9}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#64748B"
                    fontWeight="600"
                  >
                    {mod.activeRate}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Interactive Tooltip follows cursor */}
          {activeHoverBar && tooltipCoords && (
            <Paper
              elevation={6}
              sx={{
                position: 'absolute',
                left: tooltipCoords.left,
                top: tooltipCoords.top,
                p: 1.25,
                px: 1.75,
                borderRadius: '10px',
                bgcolor: '#0F3D3E',
                color: '#ffffff',
                pointerEvents: 'none',
                zIndex: 20,
                boxShadow: '0 8px 24px rgba(15, 61, 62, 0.28)',
                minWidth: 155,
                transition: 'left 0.04s ease-out, top 0.04s ease-out',
              }}
            >
              <Typography variant="caption" sx={{ color: '#EBF4F4', fontWeight: 700, display: 'block', fontSize: '0.72rem' }}>
                {activeHoverBar.moduleName} {t('dashboard.moduleSuffix') || 'Module'}
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#CBD5E1' }}>
                  {activeHoverBar.series}:
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#ffffff', ml: 1 }}>
                  {activeHoverBar.count}
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>

        {/* Dynamic Interactive Series Legend */}
        <Box display="flex" justifyContent="center" gap={3} mt={1} pt={1.5} borderTop="1px solid #F1F5F9">
          <Box display="flex" alignItems="center" gap={0.75}>
            <Box sx={{ width: 10, height: 10, bgcolor: '#0F3D3E', borderRadius: '50%' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#0F3D3E', fontSize: '0.8rem' }}>
              {t('dashboard.totalVolume') || 'Total Volume'}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.75}>
            <Box sx={{ width: 10, height: 10, bgcolor: '#059669', borderRadius: '50%' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#059669', fontSize: '0.8rem' }}>
              {t('dashboard.activeEntities') || 'Approved Entities'}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.75}>
            <Box sx={{ width: 10, height: 10, bgcolor: '#D97706', borderRadius: '50%' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#D97706', fontSize: '0.8rem' }}>
              {t('dashboard.pendingApprovals') || 'Pending Approvals'}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

ModuleComparisonChartCard.propTypes = {
  moduleHealth: PropTypes.object,
  stats: PropTypes.object,
  t: PropTypes.func.isRequired,
};

function getActivityIconInfo(activity, idx = 0) {
  const text = `${activity?.title || ''} ${activity?.description || ''} ${activity?.module || ''} ${activity?.entityType || ''}`.toLowerCase();

  if (text.includes('contact') || text.includes('phone')) {
    return { icon: <ContactPhone sx={{ fontSize: 18 }} />, bg: '#ECFDF5', color: '#059669', border: '#A7F3D0' };
  }
  if (text.includes('event')) {
    return { icon: <Event sx={{ fontSize: 18 }} />, bg: '#EEF2FF', color: '#6366F1', border: '#C7D2FE' };
  }
  if (text.includes('promo') || text.includes('offer') || text.includes('deal')) {
    return { icon: <LocalOffer sx={{ fontSize: 18 }} />, bg: '#FFFBEB', color: '#D97706', border: '#FDE68A' };
  }
  if (text.includes('accommodat') || text.includes('apartment') || text.includes('room') || text.includes('housing')) {
    return { icon: <Apartment sx={{ fontSize: 18 }} />, bg: '#F0F9FF', color: '#0284C7', border: '#BAE6FD' };
  }
  if (text.includes('user') || text.includes('person')) {
    return { icon: <People sx={{ fontSize: 18 }} />, bg: '#EBF4F4', color: '#0F3D3E', border: '#CDE3E3' };
  }

  // Fallback distinct color avatar styles
  const styles = [
    { icon: <ContactPhone sx={{ fontSize: 18 }} />, bg: '#ECFDF5', color: '#059669', border: '#A7F3D0' },
    { icon: <Event sx={{ fontSize: 18 }} />, bg: '#EEF2FF', color: '#6366F1', border: '#C7D2FE' },
    { icon: <LocalOffer sx={{ fontSize: 18 }} />, bg: '#FFFBEB', color: '#D97706', border: '#FDE68A' },
    { icon: <Apartment sx={{ fontSize: 18 }} />, bg: '#F0F9FF', color: '#0284C7', border: '#BAE6FD' },
  ];
  return styles[idx % styles.length];
}

// Recent Activity Card
function RecentActivityCard({ activities, locale, t }) {
  return (
    <Card
      sx={{
        borderRadius: '16px',
        bgcolor: '#ffffff',
        border: '1px solid #E2E8F0',
        boxShadow: '0 2px 12px rgba(15, 61, 62, 0.04)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar sx={{ bgcolor: '#EBF4F4', color: '#0F3D3E', width: 36, height: 36, borderRadius: '10px' }}>
              <Timeline sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F3D3E', fontSize: '1.02rem', lineHeight: 1.2 }}>
                {t('dashboard.recentActivity')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                {t('dashboard.recentActivitySubtitle')}
              </Typography>
            </Box>
          </Box>
        }
        action={
          <IconButton size="small">
            <MoreVert />
          </IconButton>
        }
        sx={{ p: 2.25, pb: 1.5 }}
      />
      <Divider />
      <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <List
          sx={{
            p: 0,
            flex: 1,
            maxHeight: 420,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '5px' },
            '&::-webkit-scrollbar-track': { background: '#F8FAFC', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb': { background: '#CBD5E1', borderRadius: '4px', '&:hover': { background: '#94A3B8' } },
          }}
        >
          {activities.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>
                {t('dashboard.noRecentActivity')}
              </Typography>
            </Box>
          ) : (
            activities.map((act, idx) => {
              const isPending = act.status === 'PENDING';
              const isApproved = act.status === 'APPROVED';
              const iconInfo = getActivityIconInfo(act, idx);

              return (
                <ListItem
                  key={act.id || idx}
                  alignItems="center"
                  divider
                  sx={{
                    py: 1.5,
                    px: 2.25,
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: '#F8FAFC' },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 38,
                      height: 38,
                      mr: 1.75,
                      borderRadius: '10px',
                      flexShrink: 0,
                      bgcolor: iconInfo.bg,
                      color: iconInfo.color,
                      border: `1px solid ${iconInfo.border}`,
                    }}
                  >
                    {iconInfo.icon}
                  </Avatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '0.88rem', lineHeight: 1.2 }}>
                        {act.title || 'System Action'}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 0.25 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748B',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            fontSize: '0.76rem',
                            fontWeight: 500,
                          }}
                        >
                          {act.description || 'Action submitted for review'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94A3B8', mt: 0.25, display: 'block', fontSize: '0.68rem', fontWeight: 600 }}>
                          {formatDateTime(act.createdAt, locale)}
                        </Typography>
                      </Box>
                    }
                  />
                  <Chip
                    label={act.status || 'PENDING'}
                    size="small"
                    sx={{
                      fontWeight: 800,
                      height: 22,
                      fontSize: '0.66rem',
                      px: 0.5,
                      ml: 1,
                      flexShrink: 0,
                      bgcolor: isApproved ? '#ECFDF5' : isPending ? '#FFFBEB' : '#FEF2F2',
                      color: isApproved ? '#059669' : isPending ? '#D97706' : '#DC2626',
                      border: `1px solid ${isApproved ? '#A7F3D0' : isPending ? '#FDE68A' : '#FCA5A5'}`,
                    }}
                  />
                </ListItem>
              );
            })
          )}
        </List>
      </CardContent>
    </Card>
  );
}

RecentActivityCard.propTypes = {
  activities: PropTypes.arrayOf(PropTypes.object).isRequired,
  locale: PropTypes.string,
  t: PropTypes.func.isRequired,
};

function CityStatsGrid({ cityStats, t }) {
  if (!cityStats) return null;

  return (
    <Grid container spacing={2.5} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard
          title={t('dashboard.statUser')}
          stats={cityStats.userStats}
          icon={<People sx={{ fontSize: 22 }} />}
          color="#0F3D3E"
          gradient="linear-gradient(135deg, #0F3D3E 0%, #165658 100%)"
          t={t}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard
          title={t('dashboard.statEvent')}
          stats={cityStats.eventStats}
          icon={<Event sx={{ fontSize: 22 }} />}
          color="#6366F1"
          gradient="linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)"
          t={t}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard
          title={t('dashboard.statPromotion')}
          stats={cityStats.promotionStats}
          icon={<LocalOffer sx={{ fontSize: 22 }} />}
          color="#D97706"
          gradient="linear-gradient(135deg, #D97706 0%, #F59E0B 100%)"
          t={t}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard
          title={t('dashboard.statContact')}
          stats={cityStats.contactStats}
          icon={<ContactPhone sx={{ fontSize: 22 }} />}
          color="#059669"
          gradient="linear-gradient(135deg, #059669 0%, #10B981 100%)"
          t={t}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard
          title={t('dashboard.statAccommodation')}
          stats={cityStats.accommodationStats}
          icon={<Apartment sx={{ fontSize: 22 }} />}
          color="#0284C7"
          gradient="linear-gradient(135deg, #0284C7 0%, #3B82F6 100%)"
          t={t}
        />
      </Grid>
    </Grid>
  );
}

CityStatsGrid.propTypes = {
  cityStats: PropTypes.object,
  t: PropTypes.func.isRequired,
};

function CityStatsDialog({
  open,
  onClose,
  t,
  cities,
  selectedCityId,
  onCityChange,
  cityLoading,
  cityStats,
  cityPeriod,
  cityMonth,
  cityYear,
  onCityPeriodChange,
  onCityMonthChange,
  onCityYearChange,
}) {
  const [monthAnchorEl, setMonthAnchorEl] = useState(null);
  const [yearAnchorEl, setYearAnchorEl] = useState(null);

  const availableYears = useMemo(() => {
    const startYear = 2025;
    const currentYear = new Date().getFullYear();
    const maxYear = Math.max(currentYear, startYear);
    const years = [];
    for (let y = maxYear; y >= startYear; y--) {
      years.push(y);
    }
    return years;
  }, []);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: '20px' } }}>
      <DialogTitle sx={{ fontWeight: 800, color: '#0F3D3E' }}>
        {t('dashboard.cityDialogTitle')}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{ mt: 1, mb: 2 }}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
        >
          <FormControl size="small" sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: '0 1 280px' } }}>
            <InputLabel>{t('dashboard.selectCity')}</InputLabel>
            <Select value={selectedCityId} label={t('dashboard.selectCity')} onChange={onCityChange}>
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* City Statistics Filter Pills */}
          <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
            {/* 1. Year Selector Dropdown Button */}
            {cityYear && onCityYearChange && (
              <>
                <Button
                  size="small"
                  onClick={(e) => setYearAnchorEl(e.currentTarget)}
                  endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
                  sx={{
                    bgcolor: '#ffffff',
                    color: '#0F3D3E',
                    borderRadius: '20px',
                    px: 1.75,
                    py: 0.4,
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    textTransform: 'none',
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 2px 6px rgba(15, 61, 62, 0.04)',
                    height: 30,
                    '&:hover': {
                      bgcolor: '#F1F5F9',
                      borderColor: '#0F3D3E',
                    },
                  }}
                >
                  {cityYear}
                </Button>

                <Menu
                  anchorEl={yearAnchorEl}
                  open={Boolean(yearAnchorEl)}
                  onClose={() => setYearAnchorEl(null)}
                  PaperProps={{
                    elevation: 6,
                    sx: {
                      borderRadius: '14px',
                      mt: 1,
                      minWidth: 120,
                      boxShadow: '0 10px 30px rgba(15, 61, 62, 0.15)',
                      border: '1px solid #E2E8F0',
                      p: '4px',
                      '& .MuiMenuItem-root': {
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        py: 0.85,
                        px: 1.75,
                        borderRadius: '8px',
                        color: '#0F3D3E',
                        transition: 'all 0.15s ease',
                        '&.Mui-selected': {
                          bgcolor: '#0F3D3E',
                          color: '#ffffff',
                          fontWeight: 700,
                          '&:hover': {
                            bgcolor: '#165658',
                          },
                        },
                        '&:hover': {
                          bgcolor: '#F1F5F9',
                        },
                      },
                    },
                  }}
                >
                  {availableYears.map((y) => (
                    <MenuItem
                      key={y}
                      selected={cityYear === y}
                      onClick={() => {
                        onCityYearChange(y);
                        setYearAnchorEl(null);
                      }}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {/* 2. Separate Month Dropdown Button */}
            {onCityMonthChange && (
              <>
                <Button
                  size="small"
                  onClick={(e) => {
                    if (cityPeriod !== 'month') onCityPeriodChange('month');
                    setMonthAnchorEl(e.currentTarget);
                  }}
                  endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
                  sx={{
                    bgcolor: cityPeriod === 'month' ? '#0F3D3E' : '#ffffff',
                    color: cityPeriod === 'month' ? '#ffffff' : '#0F3D3E',
                    borderRadius: '20px',
                    px: 1.75,
                    py: 0.4,
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    textTransform: 'none',
                    border: '1px solid #E2E8F0',
                    boxShadow: cityPeriod === 'month' ? '0 2px 6px rgba(15, 61, 62, 0.2)' : '0 2px 6px rgba(15, 61, 62, 0.04)',
                    height: 30,
                    '&:hover': {
                      bgcolor: cityPeriod === 'month' ? '#165658' : '#F1F5F9',
                      borderColor: '#0F3D3E',
                    },
                  }}
                >
                  {getMonthDisplayLabel(cityMonth, cityPeriod, t)}
                </Button>

                <Menu
                  anchorEl={monthAnchorEl}
                  open={Boolean(monthAnchorEl)}
                  onClose={() => setMonthAnchorEl(null)}
                  PaperProps={{
                    elevation: 6,
                    sx: {
                      borderRadius: '14px',
                      mt: 1,
                      minWidth: 170,
                      maxHeight: 320,
                      boxShadow: '0 10px 30px rgba(15, 61, 62, 0.15)',
                      border: '1px solid #E2E8F0',
                      p: '4px',
                      '& .MuiMenuItem-root': {
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        py: 0.85,
                        px: 1.75,
                        borderRadius: '8px',
                        color: '#0F3D3E',
                        transition: 'all 0.15s ease',
                        '&.Mui-selected': {
                          bgcolor: '#0F3D3E',
                          color: '#ffffff',
                          fontWeight: 700,
                          '&:hover': {
                            bgcolor: '#165658',
                          },
                        },
                        '&:hover': {
                          bgcolor: '#F1F5F9',
                        },
                      },
                    },
                  }}
                >
                  {MONTHS_OPTIONS.map((m) => (
                    <MenuItem
                      key={m.value}
                      selected={cityPeriod === 'month' && cityMonth === m.value}
                      onClick={() => {
                        onCityMonthChange(m.value);
                        if (cityPeriod !== 'month') onCityPeriodChange('month');
                        setMonthAnchorEl(null);
                      }}
                    >
                      {t(m.labelKey) || m.defaultLabel}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {/* 3. Separate Week Button */}
            <Button
              size="small"
              onClick={() => onCityPeriodChange('week')}
              sx={{
                bgcolor: cityPeriod === 'week' ? '#0F3D3E' : '#ffffff',
                color: cityPeriod === 'week' ? '#ffffff' : '#0F3D3E',
                borderRadius: '20px',
                px: 1.75,
                py: 0.4,
                fontWeight: 700,
                fontSize: '0.78rem',
                textTransform: 'none',
                border: '1px solid #E2E8F0',
                boxShadow: cityPeriod === 'week' ? '0 2px 6px rgba(15, 61, 62, 0.2)' : '0 2px 6px rgba(15, 61, 62, 0.04)',
                height: 30,
                '&:hover': {
                  bgcolor: cityPeriod === 'week' ? '#165658' : '#F1F5F9',
                  borderColor: '#0F3D3E',
                },
              }}
            >
              {t('userActivity.periodWeek') || 'Week'}
            </Button>
          </Box>
        </Box>
        {cityLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress sx={{ color: '#0F3D3E' }} />
          </Box>
        ) : (
          <CityStatsGrid cityStats={cityStats} t={t} />
        )}
      </DialogContent>
    </Dialog>
  );
}

CityStatsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  cities: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedCityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onCityChange: PropTypes.func.isRequired,
  cityLoading: PropTypes.bool.isRequired,
  cityStats: PropTypes.object,
  cityPeriod: PropTypes.string,
  cityMonth: PropTypes.string,
  cityYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onCityPeriodChange: PropTypes.func,
  onCityMonthChange: PropTypes.func,
  onCityYearChange: PropTypes.func,
};

const Dashboard = () => {
  const { t, locale } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cityDialogOpen, setCityDialogOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState('');
  const [cityStats, setCityStats] = useState(null);
  const [cityLoading, setCityLoading] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [moduleHealth, setModuleHealth] = useState(null);
  const [welcomeToastOpen, setWelcomeToastOpen] = useState(false);

  const currentMonthVal = String(new Date().getMonth() + 1);
  const [statsPeriod, setStatsPeriod] = useState('month');
  const [statsMonth, setStatsMonth] = useState(currentMonthVal);
  const [statsYear, setStatsYear] = useState(() => new Date().getFullYear());
  const [statsLoading, setStatsLoading] = useState(false);

  const [cityPeriod, setCityPeriod] = useState('month');
  const [cityMonth, setCityMonth] = useState(currentMonthVal);
  const [cityYear, setCityYear] = useState(() => new Date().getFullYear());

  const fetchStatsForPeriod = async (period = statsPeriod, month = statsMonth, year = statsYear) => {
    setStatsLoading(true);
    try {
      const periodParam = (period === 'month' && month !== 'all') ? '' : period;
      const monthParam = (period === 'month' && month !== 'all') ? month : null;
      const response = await dashboardAPI.getStatistics(periodParam, monthParam, year);
      if (response.data?.success) {
        setStats(response.data.payload);
      }
    } catch (err) {
      console.error('Error fetching statistics for period:', period, err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    if (newPeriod && newPeriod !== statsPeriod) {
      setStatsPeriod(newPeriod);
      fetchStatsForPeriod(newPeriod, statsMonth, statsYear);
    }
  };

  const handleMonthChange = (newMonth) => {
    setStatsMonth(newMonth);
    setStatsPeriod('month');
    fetchStatsForPeriod('month', newMonth, statsYear);
  };

  const handleYearChange = (newYear) => {
    if (newYear && newYear !== statsYear) {
      setStatsYear(newYear);
      fetchStatsForPeriod(statsPeriod, statsMonth, newYear);
    }
  };

  const fetchCityStats = async (cityId = selectedCityId, period = cityPeriod, month = cityMonth, year = cityYear) => {
    if (!cityId) return;
    setCityLoading(true);
    try {
      const periodParam = (period === 'month' && month !== 'all') ? '' : period;
      const monthParam = (period === 'month' && month !== 'all') ? month : null;
      const response = await dashboardAPI.getStatisticsByCity(cityId, periodParam, monthParam, year);
      if (response.data?.success) {
        setCityStats(response.data.payload);
      }
    } catch (err) {
      console.error('Error fetching city statistics:', err);
    } finally {
      setCityLoading(false);
    }
  };

  const handleCityPeriodChange = (newPeriod) => {
    setCityPeriod(newPeriod);
    fetchCityStats(selectedCityId, newPeriod, cityMonth, cityYear);
  };

  const handleCityMonthChange = (newMonth) => {
    setCityMonth(newMonth);
    setCityPeriod('month');
    fetchCityStats(selectedCityId, 'month', newMonth, cityYear);
  };

  const handleCityYearChange = (newYear) => {
    setCityYear(newYear);
    fetchCityStats(selectedCityId, cityPeriod, cityMonth, newYear);
  };

  const handleOpenCityDialog = async () => {
    try {
      const response = await citiesAPI.getCities();
      if (response.data?.success) {
        setCities(response.data.payload || []);
        if (response.data.payload?.length > 0) {
          const firstId = selectedCityId || response.data.payload[0].id;
          if (!selectedCityId) setSelectedCityId(firstId);
          await fetchCityStats(firstId, cityPeriod, cityMonth, cityYear);
        }
      }
    } catch (err) {
      console.error('Error fetching cities:', err);
    }
    setCityDialogOpen(true);
  };

  const handleCityChange = async (event) => {
    const id = event.target.value;
    setSelectedCityId(id);
    await fetchCityStats(id, cityPeriod, cityMonth, cityYear);
  };

  useEffect(() => {
    let cancelled = false;

    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const [statsRes, activityRes, categoryRes, moduleHealthRes] = await Promise.all([
          dashboardAPI.getStatistics(statsPeriod).catch(() => null),
          dashboardAPI.getRecentActivity(10).catch(() => null),
          dashboardAPI.getCategoryBreakdown().catch(() => null),
          dashboardAPI.getModuleHealth().catch(() => null),
        ]);

        if (cancelled) return;

        if (statsRes?.data?.success) {
          setStats(statsRes.data.payload);
        } else {
          setError('dashboard.errorLoad');
        }

        if (activityRes?.data?.success) {
          setRecentActivities(activityRes.data.payload || []);
        }

        if (categoryRes?.data?.success) {
          setCategoryBreakdown(categoryRes.data.payload || []);
        }

        if (moduleHealthRes?.data?.success) {
          setModuleHealth(moduleHealthRes.data.payload);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching dashboard data:', err);
        setError('dashboard.errorLoadData');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDashboardData();
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedCategoryBreakdown = useMemo(() => {
    let list = Array.isArray(categoryBreakdown) ? [...categoryBreakdown] : [];

    list = list
      .map((item) => ({
        ...item,
        categoryName: item.categoryName || item.name || item.title || item.category || 'Category',
        count: Number(item.count ?? item.total ?? 0),
      }))
      .filter((item) => !/accommodat/i.test(item.categoryName || ''));

    return list.sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [categoryBreakdown]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#0F3D3E' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Typography color="error">{formatDashboardError(error, t)}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, pb: 4 }}>
      {/* Header Row (No background box/color) */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, color: '#0F3D3E', mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            {t('dashboard.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.88rem', fontWeight: 500 }}>
            {t('dashboard.welcome')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleOpenCityDialog}
          startIcon={<LocationCity />}
          sx={{
            bgcolor: '#0F3D3E',
            color: '#ffffff',
            borderRadius: '12px',
            px: 2.5,
            py: 1,
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: '0 4px 14px rgba(15, 61, 62, 0.2)',
            '&:hover': { bgcolor: '#165658' },
          }}
        >
          {t('dashboard.cityDialogTitle')}
        </Button>
      </Box>

      {/* 1. Activity Overview - Top Full-Width Horizontal Section */}
      <Box sx={{ mb: 3.5, width: '100%' }}>
        <ActivityTrendChartCard stats={stats} t={t} />
      </Box>

      {/* 2. Middle Section: Category Breakdown on Left & Platform User Visits on Right */}
      <Grid container spacing={3} sx={{ mb: 3.5 }}>
        <Grid item xs={12} lg={4}>
          <CategoryDonutChartCard categories={sortedCategoryBreakdown} t={t} />
        </Grid>
        <Grid item xs={12} lg={8}>
          <PlatformUserVisitsChartCard stats={stats} t={t} />
        </Grid>
      </Grid>

      {/* 3. Metric Cards Grid (User, City, Event, Promotion, Contact, Accommodation) */}
      <DashboardStatsCards
        stats={stats}
        statsPeriod={statsPeriod}
        statsMonth={statsMonth}
        statsYear={statsYear}
        onPeriodChange={handlePeriodChange}
        onMonthChange={handleMonthChange}
        onYearChange={handleYearChange}
        t={t}
        onOpenCityDialog={handleOpenCityDialog}
      />

      {/* 5. Module Health Comparison & Recent Activity Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <ModuleComparisonChartCard moduleHealth={moduleHealth} stats={stats} t={t} />
        </Grid>
        <Grid item xs={12} lg={5}>
          <RecentActivityCard activities={recentActivities} locale={locale} t={t} />
        </Grid>
      </Grid>

      {/* City Stats Dialog */}
      <CityStatsDialog
        open={cityDialogOpen}
        onClose={() => setCityDialogOpen(false)}
        t={t}
        cities={cities}
        selectedCityId={selectedCityId}
        onCityChange={handleCityChange}
        cityLoading={cityLoading}
        cityStats={cityStats}
        cityPeriod={cityPeriod}
        cityMonth={cityMonth}
        cityYear={cityYear}
        onCityPeriodChange={handleCityPeriodChange}
        onCityMonthChange={handleCityMonthChange}
        onCityYearChange={handleCityYearChange}
      />

      {/* Welcome Snackbar */}
      <Snackbar
        open={welcomeToastOpen}
        autoHideDuration={4000}
        onClose={() => setWelcomeToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setWelcomeToastOpen(false)} severity="success" sx={{ width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          Welcome to Welivin Admin Portal
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
